import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './ResultsScreen.css';
import { getSearchOptionLabel, getSearchOptionCategory } from '../services/searchOptionLabels';
import { getEnhancedLawLabel } from '../services/enhancedLawLabels';
import api from '../services/api';
import { Helmet } from 'react-helmet-async';

const invert = (s) => (s === 'yes' ? 'no' : s === 'no' ? 'yes' : 'partial');
const summarise = (arr) => {
  if (arr.every((s) => s === 'yes')) return 'yes';
  if (arr.some((s) => s === 'yes' || s === 'partial')) return 'partial';
  return 'no';
};

// UPDATED: Use Tim's new API endpoint
const nearMeUrl = (zip) =>
  `https://www.lgbtnearme.org/copy-of-result/index.html?zip=${zip}&miles=25&category=All+Categories&type=pridePath`;

// FIXED: Updated negative laws set to match backend exactly
const negativeLaws = new Set([14, 15, 16, 18, 19, 21, 24, 26, 27, 31, 35, 50]);

// FIXED: Corrected law mapping to match backend
const searchOptionToLaws = {
  // Basic search options
  employmentProtection: [9, 10, 11],
  stateEmployees: [13],
  relationship: [1, 4, 5, 6, 7, 8],
  healthcare: [34, 36, 37, 38, 39, 42, 43, 35, 19],
  genderMarkers: [51, 52],
  youthProtections: [22, 23, 24, 32, 29, 33, 25, 27, 31, 26, 18],
  criminalJustice: [46, 47, 50],
  noBans: [14],
  noBathroom: [15],
  noExclusionarySexDefinition: [16],
  noReligiousExemptions: [21, 19, 18],
  
  // Advanced search options
  employmentNonDiscrimination: [9],
  housingNonDiscrimination: [10],
  publicAccommodationNonDiscrimination: [11],
  stateEmployeeNonDiscrimination: [13],
  allowLocalLaws: [14],
  noReligiousExemptionMarriage: [21],
  secondParentAdoption: [1],
  adoptionNonDiscrimination: [4],
  fosterCareNonDiscrimination: [5],
  assistedReproductionRecognition: [6],
  lgbtInclusiveSpouseForLeave: [7],
  lgbtInclusiveChildrenForLeave: [8],
  privateHealthInsuranceNonDiscrimination: [34],
  transgenderYouthCare: [35],
  shieldLawsTransgenderCare: [36],
  healthInsuranceTransgenderCoverage: [37],
  medicaidTransgenderCoverage: [38],
  stateEmployeeTransgenderBenefits: [39],
  fertilityPrivateInsurance: [42],
  fertilityMedicaid: [43],
  noReligiousExemptionMedical: [19],
  driverLicenseGenderMarker: [51],
  birthCertificateGenderMarker: [52],
  bathroomAccess: [15],
  noExclusionarySexDefinition: [16],
  studentNonDiscrimination: [22],
  antibullyingLaws: [23],
  allowLocalStudentProtections: [24],
  allowLocalAntibullyingPolicies: [24],
  lgbtInclusiveCurriculum: [25],
  noDontSayGay: [26],
  noParentalOptOut: [27],
  transgenderSportsParticipation: [29],
  noForcedOuting: [31],
  conversionTherapyBan: [32],
  childWelfareSystemProtections: [33],
  noReligiousExemptionChildWelfare: [18],
  hateCrimeLaws: [46],
  noPanicDefense: [47],
  noDragRestrictions: [50]
};

// Add law classifications mapping
const LAW_CLASSIFICATIONS = {
  1: ['sexual'], 2: ['sexual'], 3: ['sexual'], 4: ['both'], 5: ['both'],
  6: ['sexual'], 7: ['both'], 8: ['both'], 9: ['both'], 10: ['both'],
  11: ['both'], 12: ['both'], 13: ['both'], 14: ['both'], 15: ['gender'],
  16: ['gender'], 17: ['both'], 18: ['both'], 19: ['both'], 20: ['sexual'],
  21: ['sexual'], 22: ['both'], 23: ['both'], 24: ['both'], 25: ['both'],
  26: ['both'], 27: ['both'], 28: ['sexual'], 29: ['gender'], 30: ['gender'],
  31: ['gender'], 32: ['both'], 33: ['both'], 34: ['both'], 35: ['gender'],
  36: ['gender'], 37: ['gender'], 38: ['gender'], 39: ['gender'], 40: ['both'],
  41: ['both'], 42: ['both'], 43: ['both'], 44: ['both'], 45: ['both'],
  46: ['both'], 47: ['both'], 48: ['both'], 49: ['both'], 50: ['both'],
  51: ['gender'], 52: ['gender'], 53: ['gender'], 54: ['gender'], 55: ['gender']
};

// Helper function to check if a search option should be shown based on protection types
const shouldShowSearchOption = (searchOption, protections) => {
  const lawIds = searchOptionToLaws[searchOption] || [];
  
  // If no laws mapped, show the option (safe default)
  if (lawIds.length === 0) return true;
  
  // If both protections are selected, show all options
  if (protections.sexualOrientation && protections.genderIdentity) {
    return true;
  }
  
  // Check if at least one law in this option applies to the selected protection
  if (protections.sexualOrientation && !protections.genderIdentity) {
    // User selected SO only - show options that have at least one SO or both law
    return lawIds.some(lawId => {
      const classifications = LAW_CLASSIFICATIONS[lawId] || ['both'];
      return classifications.includes('sexual') || classifications.includes('both');
    });
  }
  
  if (!protections.sexualOrientation && protections.genderIdentity) {
    // User selected GI only - show options that have at least one GI or both law
    return lawIds.some(lawId => {
      const classifications = LAW_CLASSIFICATIONS[lawId] || ['both'];
      return classifications.includes('gender') || classifications.includes('both');
    });
  }
  
  return true; // Default to showing if no protections selected
};

// Helper function to get classification summary for a search option
const getSearchOptionClassificationSummary = (searchOption) => {
  const lawIds = searchOptionToLaws[searchOption] || [];
  if (lawIds.length === 0) return { text: 'No laws mapped', color: '#6B7280' };
  
  const classifications = new Set();
  lawIds.forEach(lawId => {
    const lawClassifications = LAW_CLASSIFICATIONS[lawId] || ['both'];
    lawClassifications.forEach(c => classifications.add(c));
  });
  
  if (classifications.has('both') && classifications.size === 1) {
    return { text: 'Both SO & GI', color: '#8B5CF6' };
  } else if (classifications.has('sexual') && classifications.size === 1) {
    return { text: 'Sexual Orientation only', color: '#F59E0B' };
  } else if (classifications.has('gender') && classifications.size === 1) {
    return { text: 'Gender Identity only', color: '#3B82F6' };
  } else {
    // Mixed classifications
    const types = [];
    if (classifications.has('sexual')) types.push('SO');
    if (classifications.has('gender')) types.push('GI');
    if (classifications.has('both')) types.push('Both');
    return { text: `Mixed: ${types.join(', ')}`, color: '#6B7280' };
  }
};

// NEW: Helper function for city-wide data tooltips
const getCityWideDataTooltip = (location) => {
  if (location.cityWideData) {
    return `City-wide average for ${location.city}, ${location.state_id}`;
  }
  return null;
};


// Helper function to get law points
const getLawPoints = (lawId, sexualOrientation, genderIdentity) => {
  const classifications = LAW_CLASSIFICATIONS[lawId] || ['both'];
  
  // If user selected both SO and GI
  if (sexualOrientation && genderIdentity) {
    // Laws that protect both get 2 points
    if (classifications.includes('both')) {
      return 2;
    }
    // Laws that protect only one get 1 point
    return 1;
  }
  
  // If only one protection type selected, all applicable laws get 1 point
  return 1;
};

// Helper to check if law applies to user
const lawAppliesToUser = (lawId, sexualOrientation, genderIdentity) => {
  const classifications = LAW_CLASSIFICATIONS[lawId] || ['both'];
  
  if (sexualOrientation && genderIdentity) {
    return true;
  }
  
  if (sexualOrientation && !genderIdentity) {
    return classifications.includes('sexual') || classifications.includes('both');
  }
  
  if (!sexualOrientation && genderIdentity) {
    return classifications.includes('gender') || classifications.includes('both');
  }
  
  return true;
};

// Define the mapping between basic search options and advanced search options
const basicToAdvancedMapping = {
  employmentProtection: [
    'employmentNonDiscrimination',
    'housingNonDiscrimination',
    'publicAccommodationNonDiscrimination'
  ],
  stateEmployees: ['stateEmployeeNonDiscrimination'],
  relationship: [
    'secondParentAdoption',
    'adoptionNonDiscrimination',
    'fosterCareNonDiscrimination',
    'assistedReproductionRecognition',
    'lgbtInclusiveSpouseForLeave',
    'lgbtInclusiveChildrenForLeave'
  ],
  healthcare: [
    'privateHealthInsuranceNonDiscrimination',
    'shieldLawsTransgenderCare',
    'healthInsuranceTransgenderCoverage',
    'medicaidTransgenderCoverage',
    'stateEmployeeTransgenderBenefits',
    'fertilityPrivateInsurance',
    'fertilityMedicaid',
    'transgenderYouthCare',
    'noReligiousExemptionMedical'
  ],
  genderMarkers: [
    'driverLicenseGenderMarker',
    'birthCertificateGenderMarker'
  ],
  youthProtections: [
    'studentNonDiscrimination',
    'allowLocalStudentProtections',
    'antibullyingLaws',
    'allowLocalAntibullyingPolicies',
    'conversionTherapyBan',
    'transgenderSportsParticipation',
    'childWelfareSystemProtections',
    'lgbtInclusiveCurriculum',
    'noParentalOptOut',
    'noForcedOuting',
    'noDontSayGay',
    'noReligiousExemptionChildWelfare'
  ],
  criminalJustice: [
    'hateCrimeLaws',
    'noPanicDefense',
    'noDragRestrictions'
  ],
  noBans: ['allowLocalLaws'],
  noBathroom: ['bathroomAccess'],
  noExclusionarySexDefinition: ['noExclusionarySexDefinition'],
  noReligiousExemptions: [
    'noReligiousExemptionMarriage',
    'noReligiousExemptionMedical',
    'noReligiousExemptionChildWelfare'
  ]
};

// Advanced search section headers
const advancedSectionHeaders = {
  broadProtections: "BROAD PROTECTIONS:",
  relationshipParental: "RELATIONSHIP & PARENTAL RECOGNITION:",
  healthcare: "INCLUSIVE HEALTHCARE LAWS & POLICIES:",
  genderMarkers: "GENDER MARKERS & TRANS POLICIES:",
  lgbtYouth: "LGBT YOUTH:",
  criminalJustice: "CRIMINAL JUSTICE:"
};

// Map advanced search options to their sections
const advancedOptionSections = {
  employmentNonDiscrimination: 'broadProtections',
  housingNonDiscrimination: 'broadProtections',
  publicAccommodationNonDiscrimination: 'broadProtections',
  stateEmployeeNonDiscrimination: 'broadProtections',
  allowLocalLaws: 'broadProtections',
  noReligiousExemptionMarriage: 'broadProtections',
  secondParentAdoption: 'relationshipParental',
  adoptionNonDiscrimination: 'relationshipParental',
  fosterCareNonDiscrimination: 'relationshipParental',
  assistedReproductionRecognition: 'relationshipParental',
  lgbtInclusiveSpouseForLeave: 'relationshipParental',
  lgbtInclusiveChildrenForLeave: 'relationshipParental',
  privateHealthInsuranceNonDiscrimination: 'healthcare',
  shieldLawsTransgenderCare: 'healthcare',
  healthInsuranceTransgenderCoverage: 'healthcare',
  medicaidTransgenderCoverage: 'healthcare',
  stateEmployeeTransgenderBenefits: 'healthcare',
  fertilityPrivateInsurance: 'healthcare',
  fertilityMedicaid: 'healthcare',
  transgenderYouthCare: 'healthcare',
  noReligiousExemptionMedical: 'healthcare',
  driverLicenseGenderMarker: 'genderMarkers',
  birthCertificateGenderMarker: 'genderMarkers',
  bathroomAccess: 'genderMarkers',
  noExclusionarySexDefinition: 'genderMarkers',
  studentNonDiscrimination: 'lgbtYouth',
  allowLocalStudentProtections: 'lgbtYouth',
  antibullyingLaws: 'lgbtYouth',
  allowLocalAntibullyingPolicies: 'lgbtYouth',
  conversionTherapyBan: 'lgbtYouth',
  transgenderSportsParticipation: 'lgbtYouth',
  childWelfareSystemProtections: 'lgbtYouth',
  lgbtInclusiveCurriculum: 'lgbtYouth',
  noParentalOptOut: 'lgbtYouth',
  noForcedOuting: 'lgbtYouth',
  noDontSayGay: 'lgbtYouth',
  noReligiousExemptionChildWelfare: 'lgbtYouth',
  hateCrimeLaws: 'criminalJustice',
  noPanicDefense: 'criminalJustice',
  noDragRestrictions: 'criminalJustice'
};

// Tooltip component
const Tooltip = ({ children, content, preferredSide = 'right' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [actualSide, setActualSide] = useState(preferredSide);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let side = preferredSide;
      let top = 0;
      let left = 0;
      
      const spaceRight = viewportWidth - triggerRect.right;
      const spaceLeft = triggerRect.left;
      
      if (preferredSide === 'right' && spaceRight < tooltipRect.width + 10) {
        side = spaceLeft > tooltipRect.width + 10 ? 'left' : 'top';
      } else if (preferredSide === 'left' && spaceLeft < tooltipRect.width + 10) {
        side = spaceRight > tooltipRect.width + 10 ? 'right' : 'top';
      }
      
      switch (side) {
        case 'right':
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          left = triggerRect.right + 8;
          break;
        case 'left':
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          left = triggerRect.left - tooltipRect.width - 8;
          break;
        case 'top':
          top = triggerRect.top - tooltipRect.height - 8;
          left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
          break;
        case 'bottom':
          top = triggerRect.bottom + 8;
          left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
          break;
      }
      
      top = Math.max(5, Math.min(top, viewportHeight - tooltipRect.height - 5));
      left = Math.max(5, Math.min(left, viewportWidth - tooltipRect.width - 5));
      
      setPosition({ top, left });
      setActualSide(side);
    }
  }, [isVisible, preferredSide]);

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 200);
  };

  const handleTooltipMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleTooltipMouseLeave = () => {
    setIsVisible(false);
  };

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ position: 'relative', cursor: 'help' }}
      >
        {children}
      </span>
      {isVisible && (
        <div
          ref={tooltipRef}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 9999,
            pointerEvents: 'auto'
          }}
          className={`tooltip-container ${actualSide}`}
        >
          <div className="tooltip-content">
            {content}
          </div>
          <div className={`tooltip-arrow ${actualSide}`} />
        </div>
      )}
    </>
  );
};

// MappingDebugger component - UPDATED with law classifications and scoring
const MappingDebugger = ({ formData, selectedSearchOptions, onToggleExpansion, onToggleInvertedTags, searchType, onPartialColorChange, locations, protections, populationCategories, searchMetadata, tieBreaker }) => {
  const [showDebug, setShowDebug] = useState(false);
  const [disableExpansion, setDisableExpansion] = useState(searchType === 'advanced');
  const [hideInvertedTags, setHideInvertedTags] = useState(true);
  const savedColor = typeof window !== 'undefined' ? localStorage.getItem('pridepath-partial-color') : null;
  const [partialColor, setPartialColor] = useState(savedColor || 'yellow');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showScoringDetails, setShowScoringDetails] = useState(true);
  const [showIncomeDetails, setShowIncomeDetails] = useState(false);

  const getTieBreakerDisplay = () => {
    switch(tieBreaker) {
      case 'resources':
        return 'Number of Resources';
      case 'rent':
        return 'Rental Costs';
      case 'distance':
        return 'Distance from Current Location';
      default:
        return 'Not specified';
    }
  };

  const partialColorOptions = [
    { value: 'yellow', label: 'Yellow (Default)', gradient: 'linear-gradient(135deg, #FEF3C7, #FDE68A)', textColor: '#92400E', borderColor: '#F59E0B' },
    { value: 'lightblue', label: 'Light Blue', gradient: 'linear-gradient(135deg, #DBEAFE, #BFDBFE)', textColor: '#1E40AF', borderColor: '#3B82F6' },
    { value: 'darkblue', label: 'Dark Blue', gradient: 'linear-gradient(135deg, #C7D2FE, #A5B4FC)', textColor: '#312E81', borderColor: '#6366F1' },
    { value: 'purple', label: 'Purple', gradient: 'linear-gradient(135deg, #E9D5FF, #D8B4FE)', textColor: '#581C87', borderColor: '#9333EA' },
    { value: 'orange', label: 'Orange', gradient: 'linear-gradient(135deg, #FED7AA, #FDBA74)', textColor: '#7C2D12', borderColor: '#FB923C' },
    { value: 'gray', label: 'Gray', gradient: 'linear-gradient(135deg, #F3F4F6, #E5E7EB)', textColor: '#374151', borderColor: '#9CA3AF' }
  ];

  const saveColorAsDefault = () => {
    try {
      localStorage.setItem('pridepath-partial-color', partialColor);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Failed to save color preference:', error);
    }
  };

  const resetToDefault = () => {
    setPartialColor('yellow');
    onPartialColorChange('yellow');
    try {
      localStorage.removeItem('pridepath-partial-color');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Failed to reset color preference:', error);
    }
  };

  

  useEffect(() => {
    const adjustHeaderAlignment = () => {
      const headers = document.querySelectorAll('.location-header');
      if (headers.length === 0) return;
      
      // Reset heights first
      headers.forEach(header => {
        header.style.height = 'auto';
      });
      
      // Find the tallest header
      let maxHeight = 0;
      headers.forEach(header => {
        const height = header.getBoundingClientRect().height;
        if (height > maxHeight) maxHeight = height;
      });
      
      // Apply the max height to all headers
      headers.forEach(header => {
        header.style.height = `${maxHeight}px`;
      });
    };
    
    // Adjust on mount and window resize
    adjustHeaderAlignment();
    window.addEventListener('resize', adjustHeaderAlignment);
    
    return () => {
        window.removeEventListener('resize', adjustHeaderAlignment);
      };
    }, []); // Run only on mount

  useEffect(() => {
    onToggleExpansion(disableExpansion);
  }, [disableExpansion, onToggleExpansion]);

  useEffect(() => {
    onToggleInvertedTags(hideInvertedTags);
  }, [hideInvertedTags, onToggleInvertedTags]);

  useEffect(() => {
    onPartialColorChange(partialColor);
  }, [partialColor, onPartialColorChange]);

  const getLawStatus = (location, lawId) => {
    const match = location.laws.find((l) => l.law_id === lawId);
    let status = match?.status || 'no';
    // DON'T invert - VCC data is already correct
    return status;
  };

  // UPDATED getScoringBreakdown with law classifications and point system
  const getScoringBreakdown = () => {
    const goodToHaveOptions = Object.entries(formData)
      .filter(([_, v]) => v === 'good-to-have')
      .map(([k]) => k);
    
    const mustHaveOptions = Object.entries(formData)
      .filter(([_, v]) => v === 'must-have')
      .map(([k]) => k);
    
    const breakdown = locations.map((location, idx) => {
      const locationName = idx === 0 ? 'Current Location' : `Match ${idx}`;
      
      console.log(`\n=== FRONTEND SCORE CALCULATION for ${locationName}: ${location.city}, ${location.state_id} ===`);
      console.log('Good-to-have fields:', goodToHaveOptions);
      
      // Build lawMap like backend does
      const lawMap = new Map();
      location.laws.forEach(law => {
        lawMap.set(law.law_id, law.status);
      });
      
      // Calculate score using exact backend logic
      let totalPoints = 0;
      let earnedPoints = 0;
      const countedLaws = new Set();
      const details = [];
      
      // Check must-haves first
      let meetsMustHaves = true;
      const mustHaveDetails = [];
      
      for (const option of mustHaveOptions) {
        const lawIds = searchOptionToLaws[option] || [];
        let satisfied = false;
        
        for (const lawId of lawIds) {
          if (!lawAppliesToUser(lawId, protections.sexualOrientation, protections.genderIdentity)) {
            continue;
          }
          
          let status = lawMap.get(lawId);
          if (status === undefined) status = 'no';
          
          // Apply inversion for negative laws
          if (negativeLaws.has(lawId)) {
            status = status === 'yes' ? 'no' : status === 'no' ? 'yes' : 'partial';
          }
          
          if (status === 'yes' || status === 'partial') {
            satisfied = true;
          }
        }
        
        if (!satisfied && lawIds.length > 0) {
          meetsMustHaves = false;
        }
        
        mustHaveDetails.push({
          option: getSearchOptionLabel(option),
          lawIds,
          passed: satisfied
        });
      }
      
      // Calculate good-to-have score with backend logic
      for (const option of goodToHaveOptions) {
        const lawIds = searchOptionToLaws[option] || [];
        console.log(`\nProcessing field: ${option} with laws: ${lawIds.join(', ')}`);
        
        let optionYesPoints = 0;
        let optionPossiblePoints = 0;
        const lawStatuses = [];
        
        for (const lawId of lawIds) {
          // Skip if already counted
          if (countedLaws.has(lawId)) {
            console.log(`  Law ${lawId}: SKIPPED (already counted)`);
            lawStatuses.push({ 
              lawId, 
              status: 'skipped', 
              points: 0, 
              reason: 'Already counted in another option' 
            });
            continue;
          }
          
          // Check if law applies
          if (!lawAppliesToUser(lawId, protections.sexualOrientation, protections.genderIdentity)) {
            console.log(`  Law ${lawId}: SKIPPED (doesn't apply to protections)`);
            lawStatuses.push({ 
              lawId, 
              status: 'skipped', 
              points: 0, 
              reason: 'Does not apply to selected protection types' 
            });
            continue;
          }
          
          countedLaws.add(lawId);
          
          let status = lawMap.get(lawId);
          if (status === undefined) {
            console.log(`  Law ${lawId}: No status found, defaulting to 'no'`);
            status = 'no';
          }
          
          const originalStatus = status;
          
          // Apply inversion for negative laws
          if (negativeLaws.has(lawId)) {
            status = status === 'yes' ? 'no' : status === 'no' ? 'yes' : 'partial';
            console.log(`  Law ${lawId}: ${originalStatus} → ${status} (INVERTED - negative law)`);
          } else {
            console.log(`  Law ${lawId}: ${status}`);
          }
          
          const points = getLawPoints(lawId, protections.sexualOrientation, protections.genderIdentity);
          console.log(`  Law ${lawId}: Worth ${points} point(s)`);
          
          // CRITICAL FIX: Match backend scoring logic exactly
          if (status === 'yes') {
            earnedPoints += points;
            optionYesPoints += points;
            console.log(`  Law ${lawId}: EARNED ${points} points (total earned: ${earnedPoints})`);
          }
          
          // CRITICAL FIX: Only count yes/no toward total, NOT partial
          if (status === 'yes' || status === 'no') {
            totalPoints += points;
            optionPossiblePoints += points;
            console.log(`  Law ${lawId}: COUNTED toward total (total possible: ${totalPoints})`);
          } else if (status === 'partial') {
            console.log(`  Law ${lawId}: PARTIAL STATUS - NOT COUNTED toward total`);
          }
          
          lawStatuses.push({ 
            lawId, 
            status, 
            points,
            classification: (LAW_CLASSIFICATIONS[lawId] || ['both']).join('/'),
            explanation: points === 2 ? 'Worth 2 points (protects both SO & GI)' : 'Worth 1 point'
          });
        }
        
        details.push({
          option: getSearchOptionLabel(option),
          lawIds,
          lawStatuses,
          yesPoints: optionYesPoints,
          possiblePoints: optionPossiblePoints,
          contribution: optionPossiblePoints > 0 ? (optionYesPoints / optionPossiblePoints * 100).toFixed(1) : '0.0'
        });
      }
      
      console.log('\n=== FRONTEND FINAL SUMMARY ===');
      console.log(`Total laws evaluated: ${countedLaws.size}`);
      console.log(`Laws counted: ${Array.from(countedLaws).sort((a,b) => a-b).join(', ')}`);
      console.log(`Total points possible: ${totalPoints}`);
      console.log(`Total points earned: ${earnedPoints}`);
      
      // Handle edge case
      if (totalPoints === 0) {
        totalPoints = 1;
        earnedPoints = 1;
      }
      
      const ratio = earnedPoints / totalPoints;
      console.log(`Final percentage: ${(ratio * 100).toFixed(1)}%`);
      console.log('=== END FRONTEND DEBUG ===\n');
      
      return {
        locationName,
        location,
        meetsMustHaves,
        mustHaveDetails,
        goodToHaveDetails: details,
        totalYesPoints: earnedPoints,
        totalPossiblePoints: totalPoints,
        ratio,
        ratioPercent: (ratio * 100).toFixed(1),
        countedLaws: countedLaws.size
      };
    });
    
    // Keep the tie detection code
    const scoreGroups = {};
    breakdown.forEach(item => {
      const score = item.ratioPercent;
      if (!scoreGroups[score]) scoreGroups[score] = [];
      scoreGroups[score].push(item.location);
    });
    
    const tiesFound = Object.values(scoreGroups).some(group => group.length > 1);
    
    if (tiesFound) {
      console.log(`\n🎯 TIE-BREAKER ANALYSIS (Using: ${tieBreaker}):`);
      
      Object.entries(scoreGroups).forEach(([score, locations]) => {
        if (locations.length > 1) {
          console.log(`\nCities with score ${score}%:`);
          
          const sortedByTieBreaker = [...locations].sort((a, b) => {
            switch(tieBreaker) {
              case 'resources':
                return (b.totalResources || 0) - (a.totalResources || 0);
              case 'rent':
                return (a.rent_median || Infinity) - (b.rent_median || Infinity);
              case 'distance':
                return (a.distance || Infinity) - (b.distance || Infinity);
              default:
                return 0;
            }
          });
          
          sortedByTieBreaker.forEach((city, idx) => {
            let tieBreakValue = '';
            switch(tieBreaker) {
              case 'resources':
                tieBreakValue = `${city.totalResources || 0} resources`;
                break;
              case 'rent':
                tieBreakValue = `$${city.rent_median || 'N/A'}/month`;
                break;
              case 'distance':
                tieBreakValue = `${city.distance?.toFixed(1) || 0} miles`;
                break;
            }
            console.log(`  ${idx + 1}. ${city.city}, ${city.state_id} - ${tieBreakValue}`);
          });
        }
      });
    }
    
    return breakdown;
  };

  const getSelectedPopulationCategories = () => {
    const selected = [];
    
    if (populationCategories.under10k) selected.push('Under 10k');
    if (populationCategories.from10kTo50k) selected.push('10k-50k');
    if (populationCategories.from50kTo200k) selected.push('50k-200k');
    if (populationCategories.from200kTo1m) selected.push('200k-1M');
    if (populationCategories.over1m) selected.push('Over 1M');
    
    if (selected.length === 0) return 'All sizes';
    if (selected.length === 5) return 'All sizes';
    if (selected.length === 1) return selected[0];
    if (selected.length === 2) return selected.join(' and ');
    
    const lastItem = selected.pop();
    return selected.join(', ') + ', and ' + lastItem;
  };

  if (!showDebug) {
    return (
      <div style={{ 
        background: '#e8f4fd', 
        padding: '0.75rem', 
        margin: '1rem 0', 
        borderRadius: '8px',
        textAlign: 'center',
        border: '2px solid #0066cc'
      }}>
        <button 
          onClick={() => setShowDebug(true)}
          style={{
            background: '#0066cc',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          🔍 Show Debug: Search Option → Law Mappings & Scoring
        </button>
      </div>
    );
  }

  const scoringBreakdown = getScoringBreakdown();

  // Check if any location has city-wide data
  const hasCityWideData = locations.some(loc => loc.cityWideData);

  return (
    <div style={{ 
      background: '#f8f9fa', 
      padding: '1rem', 
      margin: '0.5rem 0', 
      borderRadius: '8px',
      fontSize: '0.85rem',
      maxHeight: '600px',
      overflow: 'auto',
      border: '2px solid #0066cc'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h4 style={{ margin: 0 }}>🔍 Debug: Search Option → Law Mappings & Scoring</h4>
        <button 
          onClick={() => setShowDebug(false)}
          style={{
            background: '#dc3545',
            color: 'white',
            border: 'none',
            padding: '0.25rem 0.5rem',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.8rem'
          }}
        >
          ✕ Hide
        </button>
      </div>
      
      <div style={{ background: '#fff3cd', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem' }}>
        <strong>📊 Summary:</strong>
        <div>Total selected options: {selectedSearchOptions.length}</div>
        <div>Must Have: {Object.entries(formData).filter(([_, v]) => v === 'must-have').length}</div>
        <div>Good to Have: {Object.entries(formData).filter(([_, v]) => v === 'good-to-have').length}</div>
        {protections && (
          <div style={{ marginTop: '0.5rem', color: '#856404' }}>
            <strong>Protection Types:</strong> 
            {protections.sexualOrientation && protections.genderIdentity ? ' Both SO & GI' :
             protections.sexualOrientation ? ' Sexual Orientation only' :
             protections.genderIdentity ? ' Gender Identity only' : ' None selected'}
          </div>
        )}
        {protections.sexualOrientation && protections.genderIdentity && (
          <div style={{ marginTop: '0.5rem', color: '#856404', fontSize: '0.85rem' }}>
            <strong>⚠️ Scoring System:</strong> Laws protecting both SO & GI = 2 points, others = 1 point
          </div>
        )}
        {populationCategories && (
          <div style={{ marginTop: '0.5rem', color: '#856404' }}>
            <strong>City Sizes:</strong> {getSelectedPopulationCategories()}
          </div>
        )}
        {hasCityWideData && (
          <div style={{ marginTop: '0.5rem', color: '#6366F1', fontSize: '0.85rem' }}>
            <strong>📊 Data Note:</strong> Some locations show city-wide averages where ZIP-specific data unavailable
          </div>
        )}
        {searchMetadata && (
          <>
            <div style={{ marginTop: '0.5rem', borderTop: '1px solid #ffc107', paddingTop: '0.5rem' }}>
              <strong>🔍 Search Performance:</strong>
            </div>
            {searchMetadata.phases && (
              <div style={{ fontSize: '0.75rem' }}>
                {searchMetadata.phases.map((phase, idx) => (
                  <div key={idx}>
                    {phase.name}: {phase.duration}ms
                  </div>
                ))}
                <div style={{ fontWeight: 'bold' }}>
                  Total: {searchMetadata.totalTime}ms
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Income/Rent Calculation Details */}
      <div style={{ background: '#F0F9FF', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <strong>💰 Income & Rent Calculation Details:</strong>
          <button
            onClick={() => setShowIncomeDetails(!showIncomeDetails)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.8rem',
              color: '#0284C7'
            }}
          >
            {showIncomeDetails ? '▼ Hide' : '▶ Show'}
          </button>
        </div>
        
        {showIncomeDetails && (
          <div style={{ marginTop: '0.5rem' }}>
            {locations.map((loc, idx) => (
              <div key={idx} style={{ 
                marginBottom: '1rem', 
                padding: '0.5rem', 
                background: idx === 0 ? '#FFF8E1' : '#E3F2FD',
                borderRadius: '4px',
                border: `1px solid ${idx === 0 ? '#FFC107' : '#2196F3'}`
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  📍 {idx === 0 ? 'Current Location' : `Match ${idx}`}: {loc.city}, {loc.state_id}
                </div>
                
                {loc.cityWideData ? (
                  <div style={{ fontSize: '0.8rem' }}>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Calculation Method:</strong> {loc.isWeightedAverage ? 
                        '📊 Weighted Average (Population-based)' : 
                        '📈 Simple Average (No population data)'}
                    </div>
                    
                    {loc.isWeightedAverage ? (
                      <>
                        <div style={{ marginLeft: '1rem', color: '#059669' }}>
                          <div>✅ Total City Population: {loc.cityPopulation?.toLocaleString() || 'N/A'}</div>
                          <div>✅ Population with Income Data: {loc.incomePopulation?.toLocaleString() || 'N/A'}</div>
                          <div>✅ Population with Rent Data: {loc.rentPopulation?.toLocaleString() || 'N/A'}</div>
                          <div>✅ ZCTA ZIP Codes Used: {loc.zctaCount || 'N/A'}</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ marginLeft: '1rem', color: '#DC2626' }}>
                          <div>⚠️ No population data available - using simple average</div>
                          <div>📍 Income data points: {loc.incomeDataPoints || 0}</div>
                          <div>📍 Rent data points: {loc.rentDataPoints || 0}</div>
                          <div>📍 ZCTA ZIP Codes Found: {loc.zctaCount || 0}</div>
                        </div>
                      </>
                    )}
                    
                    <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #E5E7EB' }}>
                      <strong>Results:</strong>
                      <div style={{ marginLeft: '1rem' }}>
                        <div>💵 Median Income: ${loc.median_household_income?.toLocaleString() || 'No Data'}</div>
                        <div>🏠 Median Rent: ${loc.rent_median?.toLocaleString() || 'No Data'}</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: '0.8rem', color: '#DC2626' }}>
                    ❌ No city-wide data available for this location
                  </div>
                )}
              </div>
            ))}
            
            <div style={{ 
              fontSize: '0.75rem', 
              color: '#6B7280', 
              marginTop: '0.5rem',
              padding: '0.5rem',
              background: '#F9FAFB',
              borderRadius: '4px'
            }}>
              <strong>ℹ️ About the calculations:</strong>
              <ul style={{ margin: '0.25rem 0 0 1rem', paddingLeft: '1rem' }}>
                <li><strong>Weighted Average:</strong> Uses population data to give more weight to ZIP codes with more residents</li>
                <li><strong>Simple Average:</strong> Used when population data is missing (all ZIP codes weighted equally)</li>
                <li><strong>ZCTA ZIP Codes:</strong> Only ZIP Code Tabulation Areas with census data are included</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {scoringBreakdown.some(loc => 
        scoringBreakdown.filter(other => 
          other.ratioPercent === loc.ratioPercent
        ).length > 1
      ) && tieBreaker && (
        <div style={{ 
          background: '#E0E7FF', 
          padding: '0.75rem', 
          borderRadius: '4px', 
          marginBottom: '1rem',
          border: '1px solid #6366F1'
        }}>
          <strong>🎯 Tie-Breaking Applied:</strong>
          <div style={{ marginTop: '0.5rem' }}>
            <strong>User Preference:</strong> {getTieBreakerDisplay()}
          </div>
          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
            Multiple cities have identical protection scores. Results are ordered by {
              tieBreaker === 'resources' ? 'highest number of resources' :
              tieBreaker === 'rent' ? 'lowest rental costs' :
              'closest distance'
            }.
          </div>
        </div>
      )}

      <div style={{ background: '#e8f5e9', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <strong>📈 Scoring Breakdown by Location:</strong>
          <button
            onClick={() => setShowScoringDetails(!showScoringDetails)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.8rem',
              color: '#2e7d32'
            }}
          >
            {showScoringDetails ? '▼ Hide' : '▶ Show'}
          </button>
        </div>
        
        {showScoringDetails && (
          <div style={{ marginTop: '0.5rem' }}>
            {scoringBreakdown.map((loc, idx) => (
              <div key={idx} style={{ 
                marginBottom: '1rem', 
                padding: '0.5rem', 
                background: idx === 0 ? '#fff8e1' : '#e3f2fd',
                borderRadius: '4px',
                border: `1px solid ${idx === 0 ? '#ffc107' : '#2196f3'}`
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  📍 {loc.locationName} - {loc.location.city}, {loc.location.state_id}
                  {loc.location.community_type && (
                    <span style={{ 
                      marginLeft: '0.5rem', 
                      fontSize: '0.75rem', 
                      color: '#666',
                      fontWeight: 'normal'
                    }}>
                      ({loc.location.community_type})
                    </span>
                  )}
                </div>
                
                {/* Add scoring explanation */}
                {protections.sexualOrientation && protections.genderIdentity && (
                  <div style={{ 
                    background: '#e8f5e9', 
                    padding: '0.5rem', 
                    borderRadius: '4px', 
                    marginBottom: '0.5rem',
                    fontSize: '0.75rem'
                  }}>
                    <strong>📊 Scoring Note:</strong> Since you selected both SO & GI protections:
                    <ul style={{ margin: '0.25rem 0 0 1rem', paddingLeft: '1rem' }}>
                      <li>Laws protecting <strong>both</strong> SO & GI = 2 points each</li>
                      <li>Laws protecting <strong>only one</strong> = 1 point each</li>
                    </ul>
                  </div>
                )}
                
                {loc.mustHaveDetails.length > 0 && (
                  <div style={{ marginBottom: '0.5rem', fontSize: '0.8rem' }}>
                    <strong>Must-Have Requirements:</strong> {loc.meetsMustHaves ? '✅ PASSED' : '❌ FAILED'}
                    {!loc.meetsMustHaves && (
                      <ul style={{ margin: '0.25rem 0', paddingLeft: '1.5rem' }}>
                        {loc.mustHaveDetails.filter(d => !d.passed).map((detail, i) => (
                          <li key={i} style={{ color: '#d32f2f' }}>
                            {detail.option}: {detail.statuses.join(', ')}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                
                <div style={{ fontSize: '0.8rem' }}>
                  <strong>Good-to-Have Scoring:</strong>
                  <div style={{ 
                    background: 'white', 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '4px',
                    marginTop: '0.25rem',
                    marginBottom: '0.5rem'
                  }}>
                    <strong>Final Ratio: {loc.ratioPercent}%</strong> ({loc.totalYesPoints}/{loc.totalPossiblePoints} points)
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: '#666' }}>
                      [{loc.countedLaws} unique laws scored]
                    </span>
                  </div>
                  
                  {loc.goodToHaveDetails.map((detail, i) => (
                    <div key={i} style={{ 
                      marginLeft: '1rem', 
                      marginBottom: '0.25rem',
                      fontSize: '0.75rem',
                      borderLeft: '2px solid #ccc',
                      paddingLeft: '0.5rem'
                    }}>
                      <div><strong>{detail.option}</strong></div>
                      <div>
                        Laws: {detail.lawStatuses.map(ls => {
                          if (ls.status === 'skipped') {
                            return `#${ls.lawId}(${ls.reason})`;
                          }
                          return `#${ls.lawId}(${ls.status}${ls.points === 2 ? ',2pts' : ''})`;
                        }).join(', ')}
                      </div>
                      <div>
                        Contribution: {detail.yesPoints}/{detail.possiblePoints} points ({detail.contribution}%)
                      </div>
                      {/* Show point breakdown for laws worth 2 points */}
                      {detail.lawStatuses.some(ls => ls.points === 2) && (
                        <div style={{ color: '#1976d2', marginTop: '0.25rem' }}>
                          💡 {detail.lawStatuses.filter(ls => ls.points === 2).length} laws worth 2 points each (protect both SO & GI)
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {loc.location.debugInfo && (
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: '#1976d2', 
                    marginTop: '0.5rem',
                    background: '#e3f2fd',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px'
                  }}>
                    <strong>Backend Score:</strong> {loc.location.debugInfo.ratioPercent} 
                    ({loc.location.debugInfo.yesPoints}/{loc.location.debugInfo.totalPossiblePoints} points)
                  </div>
                )}
                
                {idx > 0 && loc.location.distance && (
                  <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                    Distance: {loc.location.distance} miles
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ background: '#e7f3ff', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem' }}>
        <strong>⚙️ Display Settings:</strong>
        <div style={{ marginTop: '0.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', cursor: 'pointer' }}>
            <input 
              type="checkbox"
              checked={disableExpansion}
              onChange={(e) => setDisableExpansion(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span>Disable "View Details" expansion (hide law-level details)</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', cursor: 'pointer' }}>
            <input 
              type="checkbox"
              checked={hideInvertedTags}
              onChange={(e) => setHideInvertedTags(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span>Hide "(inverted)" tags for negative laws</span>
          </label>
          
          <div style={{ marginTop: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              🎨 Partial Status Color:
            </label>
            <select 
              value={partialColor}
              onChange={(e) => setPartialColor(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #ccc',
                background: 'white',
                cursor: 'pointer'
              }}
            >
              {partialColorOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            
            <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>Preview:</span>
              <div style={{
                background: partialColorOptions.find(opt => opt.value === partialColor)?.gradient,
                border: `2px solid ${partialColorOptions.find(opt => opt.value === partialColor)?.borderColor}`,
                color: partialColorOptions.find(opt => opt.value === partialColor)?.textColor,
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}>
                PARTIAL
              </div>
            </div>
            
            <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={saveColorAsDefault}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '0.8rem'
                }}
              >
                💾 Set as Default
              </button>
              <button
                onClick={resetToDefault}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '0.8rem'
                }}
              >
                ↺ Reset to Yellow
              </button>
            </div>
            
            {saveSuccess && (
              <div style={{
                marginTop: '0.5rem',
                padding: '0.5rem',
                background: '#d1fae5',
                color: '#065f46',
                borderRadius: '4px',
                textAlign: 'center',
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}>
                ✓ Preference saved successfully!
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedSearchOptions.map(searchOption => {
        const lawIds = searchOptionToLaws[searchOption] || [];
        const priority = formData[searchOption];
        
        return (
          <div key={searchOption} style={{ 
            marginBottom: '1.5rem', 
            borderBottom: '1px solid #ddd', 
            paddingBottom: '1rem',
            background: priority === 'must-have' ? '#fff3e0' : '#f8f9fa',
            padding: '0.75rem',
            borderRadius: '4px',
            border: priority === 'must-have' ? '2px solid #f59e0b' : '1px solid #e9ecef'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              {priority === 'must-have' && <span>💔 *Deal Breaker*</span>}
              <strong style={{ color: priority === 'must-have' ? '#f59e0b' : '#0066cc' }}>
                {getSearchOptionLabel(searchOption)}
              </strong>
              <span style={{ 
                background: priority === 'must-have' ? '#f59e0b' : '#6c757d',
                color: 'white',
                padding: '0.2rem 0.5rem',
                borderRadius: '12px',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                fontWeight: 'bold'
              }}>
                {priority}
              </span>
            </div>
            
            <div style={{ marginLeft: '1rem', color: '#666', fontSize: '0.75rem' }}>
              <strong>Search Option Key:</strong> <code style={{ background: '#f1f3f4', padding: '0.2rem 0.4rem', borderRadius: '3px' }}>{searchOption}</code>
            </div>
            
            <div style={{ marginLeft: '1rem', marginTop: '0.5rem' }}>
              <strong>Mapped Laws ({lawIds.length}):</strong>
              {lawIds.length > 0 ? (
                <div style={{ marginTop: '0.5rem' }}>
                  {lawIds.map(lawId => (
                    <div key={lawId} style={{ 
                      margin: '0.5rem 0', 
                      padding: '0.5rem',
                      background: 'white',
                      borderRadius: '4px',
                      border: '1px solid #e9ecef'
                    }}>
                      <div style={{ fontWeight: 'bold', color: '#495057' }}>
                        Law {lawId}: {getEnhancedLawLabel(lawId)}
                      </div>
                      {searchOption === 'driverLicenseGenderMarker' && (
                        <div style={{ fontSize: '0.7rem', color: '#0066cc', marginTop: '0.25rem' }}>
                          <strong>Status by Location:</strong>
                          <ul style={{ margin: '0.25rem 0', paddingLeft: '1rem' }}>
                            {locations.map((loc, idx) => (
                              <li key={idx}>
                                {idx === 0 ? 'Current' : `Match ${idx}`}: {getLawStatus(loc, lawId)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {negativeLaws.has(lawId) && (
                        <div style={{ fontSize: '0.7rem', color: '#dc3545', fontWeight: 'bold' }}>
                          ⚠️ NEGATIVE LAW (status inverted)
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <span style={{ color: 'red', fontWeight: 'bold' }}> ❌ NO LAWS MAPPED!</span>
              )}
            </div>
          </div>
        );
      })}
      
      <div style={{ background: '#d1ecf1', padding: '0.75rem', borderRadius: '4px', marginTop: '1rem' }}>
        <strong>💡 How to verify mappings:</strong>
        <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem', fontSize: '0.8rem' }}>
          <li>Check if law descriptions match the search option purpose</li>
          <li>Confirm negative laws (⚠️) show inverted status</li>
          <li>Look for any "❌ MISSING" labels that need attention</li>
          <li>Use display settings above to control user-facing features</li>
          <li>For driverLicenseGenderMarker, check Status by Location in debug for law ID 51</li>
          <li>Review the Scoring Breakdown to verify ratios are calculated correctly</li>
          <li>Community type filters are shown in the summary</li>
          <li>Backend scores from the API are shown in blue boxes for comparison</li>
        </ul>
      </div>
    </div>
  );
};

// Continue with the rest of the ResultsScreen component...
const ResultsScreen = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const zip = state?.zipCode;
  const searchType = state?.searchType || 'basic';
  const tieBreaker = state?.tieBreaker || '';
  
  const protections = useMemo(() => 
    state?.protections || { sexualOrientation: false, genderIdentity: false },
    [state?.protections]
  );
  
  const communityTypes = useMemo(() => 
    state?.communityTypes || { urban: false, suburban: false, rural: false },
    [state?.communityTypes]
  );
  
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStage, setLoadingStage] = useState('Initializing...');
  const [printMode, setPrintMode] = useState(false);
  const [openSearchOptions, setOpenSearchOptions] = useState({});
  const [disableExpansion, setDisableExpansion] = useState(searchType === 'advanced');
  const [hideInvertedTags, setHideInvertedTags] = useState(true);
  const savedPartialColor = typeof window !== 'undefined' ? localStorage.getItem('pridepath-partial-color') : null;
  const [partialColorScheme, setPartialColorScheme] = useState(savedPartialColor || 'yellow');
  const [isHeaderStuck, setIsHeaderStuck] = useState(false);
  const [preloadStatus, setPreloadStatus] = useState(null);
  
  // NEW: State for async resource loading
  const [resourcesLoading, setResourcesLoading] = useState({});
  const [resourcesData, setResourcesData] = useState({});

  // Check preload status if we have a sessionId
  useEffect(() => {
    if (!state?.sessionId || !loading) return;
    
    let intervalId;
    const checkPreloadStatus = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'https://pridepath-webapp.onrender.com';
        const response = await fetch(`${API_URL}/api/preload-status/${state.sessionId}`);
        if (response.ok) {
          const status = await response.json();
          setPreloadStatus(status);
        }
      } catch (error) {
        console.error('Failed to check preload status:', error);
      }
    };
    
    // Check immediately
    checkPreloadStatus();
    
    // Then check every 2 seconds while loading
    intervalId = setInterval(checkPreloadStatus, 2000);
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [state?.sessionId, loading]);

  // NEW: Fetch resources after initial data load
  useEffect(() => {
    if (data && (data.current || data.matches)) {
      const fetchAllResources = async () => {
        const allLocations = [data.current, ...(data.matches || [])].filter(Boolean);
        const loadingState = {};
        const initialResourcesData = {};
        
        // Set all to loading initially AND initialize empty data
        allLocations.forEach(loc => {
          if (loc?.zip) {
            loadingState[loc.zip] = true;
            // Initialize with undefined to indicate "not loaded yet"
            initialResourcesData[loc.zip] = {
              totalResources: undefined,
              communityCenters: undefined
            };
          }
        });
        
        // Set both states immediately so UI shows loading
        setResourcesLoading(loadingState);
        setResourcesData(initialResourcesData);
        
        // Fetch resources for each location in parallel
        const resourcePromises = allLocations.map(async (location) => {
          if (!location?.zip) return null;
          
          try {
            const response = await fetch(
              `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/lgbtnearme-resources/${location.zip}`,
              { 
                signal: AbortSignal.timeout(10000) // 10 second timeout
              }
            );
            
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();

            // Add debugging to see what the API returns
            console.log(`API response for ${location.zip}:`, data);

            // Fix the resource count extraction
            const resourceCount = 
              data?.total_resources_within_25_miles ||
              data?.totalResourcesWithin25Miles ||
              data?.total_resources ||
              data?.totalResources ||
              data?.resources?.length ||
              0;

            // Fix the community centers extraction - this is the key fix
            const centers = 
              data?.community_centers || 
              data?.communityCenters || 
              data?.centers ||
              [];

            return {
              zip: location.zip,
              totalResources: resourceCount,
              communityCenters: centers  // This will now have the correct data
            };
          } catch (error) {
            console.error(`Failed to fetch resources for ${location.zip}:`, error);
            // Return with zeros (not undefined) to indicate load completed but failed
            return {
              zip: location.zip,
              totalResources: 0,
              communityCenters: []
            };
          }
        });
        
        // Process results as they come in
        const results = await Promise.allSettled(resourcePromises);
        
        const newResourcesData = {};
        const newLoadingState = {};
        
        results.forEach((result) => {
          if (result.status === 'fulfilled' && result.value?.zip) {
            newResourcesData[result.value.zip] = {
              totalResources: result.value.totalResources,
              communityCenters: result.value.communityCenters
            };
            newLoadingState[result.value.zip] = false;
          } else if (result.status === 'rejected') {
            // Handle rejected promises
            const zip = allLocations.find(loc => 
              result.reason?.message?.includes(loc.zip)
            )?.zip;
            
            if (zip) {
              newResourcesData[zip] = {
                totalResources: 0,
                communityCenters: []
              };
              newLoadingState[zip] = false;
            }
          }
        });
        
        setResourcesData(newResourcesData);
        setResourcesLoading(newLoadingState);
      };
      
      // Start fetching immediately (no delay needed since we set loading states first)
      fetchAllResources();
    }
  }, [data]);

  console.log("ResultsScreen - Received protections:", protections);
  console.log("ResultsScreen - Received formData:", state?.formData);

  // Fix for formData - handle both the old structure and the simple structure
  const formData = useMemo(() => {
    const rawFormData = state?.formData || {};
    const processedFormData = {};
    
    // Convert from the old structure if needed
    Object.entries(rawFormData).forEach(([key, value]) => {
      if (typeof value === 'object' && value.value) {
        // Old structure: {value: 'must-have', laws: [...]}
        processedFormData[key] = value.value;
      } else if (typeof value === 'string') {
        // New simple structure: 'must-have'
        processedFormData[key] = value;
      }
    });
    
    return processedFormData;
  }, [state?.formData]);

  const mustHaveFields = useMemo(() => 
    Object.entries(formData).filter(([_, v]) => v === 'must-have').map(([k]) => k),
    [formData]
  );
  const goodToHaveFields = useMemo(() => 
    Object.entries(formData).filter(([_, v]) => v === 'good-to-have').map(([k]) => k),
    [formData]
  );
  const selectedSearchOptions = useMemo(() => 
    Object.entries(formData)
      .filter(([_, value]) => value !== null && value !== 'doesnt-matter')
      .map(([key]) => key),
    [formData]
  );

  const populationCategories = useMemo(() => 
    state?.populationCategories || {},
    [state?.populationCategories]
  );

  const getSelectedPopulationCategories = () => {
    const selected = [];
    
    if (populationCategories.under10k) selected.push('Under 10k');
    if (populationCategories.from10kTo50k) selected.push('10k-50k');
    if (populationCategories.from50kTo200k) selected.push('50k-200k');
    if (populationCategories.from200kTo1m) selected.push('200k-1M');
    if (populationCategories.over1m) selected.push('Over 1M');
    
    if (selected.length === 0) return 'All sizes';
    if (selected.length === 5) return 'All sizes';
    if (selected.length === 1) return selected[0];
    if (selected.length === 2) return selected.join(' and ');
    
    // For 3 or more selections, use commas with "and" before the last item
    const lastItem = selected.pop();
    return selected.join(', ') + ', and ' + lastItem;
  };

  const getSelectedProtectionTypes = () => {
    if (protections.sexualOrientation && protections.genderIdentity) {
      return 'Both Sexual Orientation & Gender Identity';
    } else if (protections.sexualOrientation) {
      return 'Sexual Orientation';
    } else if (protections.genderIdentity) {
      return 'Gender Identity';
    }
    return 'No protection types selected';
  };

  const getTieBreakerDisplay = () => {
    switch(tieBreaker) {
      case 'resources':
        return 'Number of Resources';
      case 'rent':
        return 'Rental Costs';
      case 'distance':
        return 'Distance from Current Location';
      default:
        return 'Not specified';
    }
  };

  const getPartialColorStyles = () => {
    const colorSchemes = {
      yellow: { background: '#FEF3C7', border: '#F59E0B', text: '#92400E' },
      lightblue: { background: '#DBEAFE', border: '#3B82F6', text: '#1E40AF' },
      darkblue: { background: '#C7D2FE', border: '#6366F1', text: '#312E81' },
      purple: { background: '#E9D5FF', border: '#9333EA', text: '#581C87' },
      orange: { background: '#FED7AA', border: '#FB923C', text: '#7C2D12' },
      gray: { background: '#F3F4F6', border: '#9CA3AF', text: '#374151' }
    };
    return colorSchemes[partialColorScheme] || colorSchemes.yellow;
  };

  // FIXED: getLawStatus now properly inverts negative laws
  const getLawStatus = (location, lawId) => {
    const match = location.laws.find((l) => l.law_id === lawId);
    let status = match?.status || 'no';
    // DON'T invert - VCC data is already correct
    return status;
  };

  const getSearchOptionStatus = (location, searchOption) => {
    const lawIds = searchOptionToLaws[searchOption] || [];
    const statuses = lawIds.map((lawId) => {
      const status = getLawStatus(location, lawId);
      return status;
    });
    
    // Use summarise to get overall status
    const result = summarise(statuses);
    return result;
  };

  const getScore = (location) => {
    if (location.debugInfo) {
      return `${location.debugInfo.yesPoints}/${location.debugInfo.totalPossiblePoints} (${location.debugInfo.ratioPercent})`;
    }
    
    const goodToHaveOptions = Object.entries(formData)
      .filter(([_, v]) => v === 'good-to-have')
      .map(([k]) => k);
    
    let totalYesPoints = 0;
    let totalPossiblePoints = 0;
    
    goodToHaveOptions.forEach(option => {
      const lawIds = searchOptionToLaws[option] || [];
      lawIds.forEach(lawId => {
        const match = location.laws.find((l) => l.law_id === lawId);
        let status = match?.status || 'no';
        
        if (negativeLaws.has(lawId)) {
          status = invert(status);
        }
        
        if (status === 'yes') {
          totalYesPoints += 1;
        }
        if (status === 'yes' || status === 'no') {
          totalPossiblePoints += 1;
        }
      });
    });
    
    const percentage = totalPossiblePoints > 0 ? ((totalYesPoints / totalPossiblePoints) * 100).toFixed(1) : '0.0';
    return `${totalYesPoints}/${totalPossiblePoints} (${percentage}%)`;
  };

  const toggleSearchOption = (searchOption) => {
    if (disableExpansion) return;
    setOpenSearchOptions((p) => ({ ...p, [searchOption]: !p[searchOption] }));
  };

  // NEW: Helper function for resource display with loading state
  const getResourceDisplayWithLoading = (col) => {
    const isLoading = resourcesLoading[col.zip];
    const resourceData = resourcesData[col.zip];
    
    if (isLoading) {
      return (
        <span style={{ 
          display: 'inline-flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          color: '#6B7280',
          fontSize: '0.875rem'
        }}>
          <span className="resource-loading-spinner"></span>
          Loading...
        </span>
      );
    }
    
    const count = resourceData?.total_resources_within_25_miles ?? 
    resourceData?.totalResources ?? 
    col.totalResources;

    // Show loading if we don't have data yet and haven't started loading
    if (count === undefined || count === null) {
      return (
        <span style={{ 
          display: 'inline-flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          color: '#9CA3AF',
          fontSize: '0.875rem',
          fontStyle: 'italic'
        }}>
          Loading resources...
        </span>
      );
    }
    
    if (count >= 0) {
      return `${count} Resource${count !== 1 ? 's' : ''}`;
    }
    return 'View Resources';
  };

  // NEW: Helper function for community centers with loading state - FIXED
  const getCommunityCentersWithLoading = (col) => {
    const isLoading = resourcesLoading[col.zip];
    const resourceData = resourcesData[col.zip];
    
    // FIXED: Check both field names for community centers
    const centers = resourceData?.communityCenters ?? 
                   resourceData?.community_centers ?? 
                   col.communityCenters ??
                   col.community_centers;
    
    if (isLoading) {
      return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: '0.5rem',
          color: '#6B7280',
          fontSize: '0.875rem',
          minHeight: '24px'
        }}>
          <span className="resource-loading-spinner"></span>
          <span>Loading...</span>
        </div>
      );
    }
    
    // Check if we haven't received data yet (not loading, but no data)
    if (centers === undefined && !resourceData) {
      return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: '0.5rem',
          color: '#9CA3AF',
          fontSize: '0.875rem',
          fontStyle: 'italic',
          minHeight: '24px'
        }}>
          Loading centers...
        </div>
      );
    }
    
    if (!centers || centers.length === 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ color: '#666', fontSize: '0.875rem' }}>
            None Available
          </span>
          
            {/* <a
            href={`https://www.lgbtnearme.org/copy-of-result/index.html?zip=${col.zip}&miles=25&category=Community+Center&type=pridePath`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#2563eb',
              textDecoration: 'underline',
              fontSize: '0.75rem',
              fontStyle: 'italic',
            }}
          >
            
          </a> */}
        </div>
      );
    }
    
    // If multiple centers or special marker
    if (centers.length > 1 || centers[0] === 'lgbtnearme_link') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          
            <a
            href={`https://www.lgbtnearme.org/copy-of-result/index.html?zip=${col.zip}&miles=25&category=Community+Center&type=pridePath`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#2563eb',
              textDecoration: 'underline',
              fontSize: '0.875rem',
              fontWeight: '400',
            }}
          >
            {centers.length > 1 ? 'Multiple' : 'View Community Centers'}
          </a>
        </div>
      );
    }
    
    // Single community center - show direct link
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        
          <a
          href={centers[0].website?.startsWith('http') ? centers[0].website : `https://${centers[0].website}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: '#2563eb',
            textDecoration: 'underline',
            fontSize: '0.875rem',
            fontWeight: '400',
          }}
        >
          {centers[0].name}
        </a>
      </div>
    );
  };

  const getResourceDisplay = (col) => {
    const count = col.totalResources;
    // If we have an actual count, display it
    if (count !== null && count !== undefined && count >= 0) {
      return `${count} Resource${count !== 1 ? 's' : ''}`;
    }
    // Fallback - when we don't have a count
    return 'View Resources';
  };

  const getPrioritizedSearchOptionRows = () => {
    // Filter out search options that shouldn't be shown based on protection types
    const filteredSearchOptions = selectedSearchOptions.filter(option => 
      shouldShowSearchOption(option, protections)
    );

    if (searchType === 'advanced') {
      const sections = [];
      const processedOptions = new Set();
      
      ['broadProtections', 'relationshipParental', 'healthcare', 'genderMarkers', 'lgbtYouth', 'criminalJustice'].forEach(sectionKey => {
        const sectionOptions = filteredSearchOptions.filter(option => 
          advancedOptionSections[option] === sectionKey && !processedOptions.has(option)
        );
        
        if (sectionOptions.length > 0) {
          sections.push({
            header: advancedSectionHeaders[sectionKey],
            options: sectionOptions.map(option => {
              processedOptions.add(option);
              return {
                searchOption: option,
                label: getSearchOptionLabel(option),
                isDealBreaker: mustHaveFields.includes(option),
                isGoodToHave: goodToHaveFields.includes(option),
                lawIds: searchOptionToLaws[option] || [],
                priority: formData[option]
              };
            })
          });
        }
      });
      
      return sections;
    } else {
      const searchOptionRows = filteredSearchOptions.map((searchOption) => ({
        searchOption,
        label: getSearchOptionLabel(searchOption),
        isDealBreaker: mustHaveFields.includes(searchOption),
        isGoodToHave: goodToHaveFields.includes(searchOption),
        lawIds: searchOptionToLaws[searchOption] || [],
        priority: formData[searchOption]
      }));
      return searchOptionRows;
    }
  };

  // ResultsScreen.js
  const handlePrint = () => {
    document.body.classList.add('print-mode');   // NEW
    setPrintMode(true);

    setTimeout(() => {
      window.print();
      setTimeout(() => {
        setPrintMode(false);
        document.body.classList.remove('print-mode'); // NEW
      }, 500);
    }, 300);
  };

  useEffect(() => {
    let capturedStyles = null;
    let isCapturing = false;
  
    const captureTableStyles = () => {
      if (isCapturing) return capturedStyles;
      isCapturing = true;
  
      const tableContainer = document.querySelector('.results-table-container');
      const mainTable = document.querySelector('.results-table');
      const resultsCard = document.querySelector('.results-card');
      
      if (!tableContainer || !mainTable || !resultsCard) {
        isCapturing = false;
        return null;
      }
      
      void tableContainer.offsetHeight;
      
      const containerRect = tableContainer.getBoundingClientRect();
      const containerStyles = window.getComputedStyle(tableContainer);
      
      const containerWidth = containerRect.width;
      
      const viewportCenter = window.innerWidth / 2;
      const containerCenter = containerRect.left + (containerRect.width / 2);
      const centerOffset = containerCenter - viewportCenter;
      
      const tableWidth = mainTable.getBoundingClientRect().width;
      
      isCapturing = false;
      
      return {
        width: containerWidth,
        tableWidth: tableWidth,
        centerOffset: centerOffset,
        containerBorder: containerStyles.borderLeftWidth + ' ' + containerStyles.borderRightWidth
      };
    };
  
    const handleScroll = () => {
      const tableContainer = document.querySelector('.results-table-container');
      const stickyWrapper = document.querySelector('.sticky-header-wrapper');
      
      if (!tableContainer) return;
      
      const rect = tableContainer.getBoundingClientRect();
      const shouldStick = rect.top <= 0 && rect.bottom > 80;
      
      if (shouldStick !== isHeaderStuck) {
        if (shouldStick && !capturedStyles) {
          // Capture styles only once when first sticking
          capturedStyles = captureTableStyles();
          
          // Apply styles immediately
          if (stickyWrapper && capturedStyles) {
            // Set exact width to match container
            stickyWrapper.style.width = `${capturedStyles.width}px`;
            stickyWrapper.style.left = '50%';
            
            // NEW: Determine scale based on viewport width
            const viewportWidth = window.innerWidth;
            let scale = 1;
            
            if (viewportWidth >= 1920) {
              scale = 1.2; // 20% bigger on large monitors
            } else if (viewportWidth >= 1600) {
              scale = 1.1; // 10% bigger on medium-large monitors
            }
            // No scaling for MacBook size (< 1600px)
            
            stickyWrapper.style.transform = `translateX(calc(-50% + ${capturedStyles.centerOffset}px)) scale(${scale})`;
            
            const stickyTable = stickyWrapper.querySelector('.sticky-table');
            if (stickyTable) {
              // Set exact table width
              stickyTable.style.width = `${capturedStyles.tableWidth}px`;
              stickyTable.style.maxWidth = `${capturedStyles.tableWidth}px`;
            }
          }
          
          tableContainer.classList.add('header-stuck');
        } else if (!shouldStick) {
          // Reset when unsticking
          capturedStyles = null;
          tableContainer.classList.remove('header-stuck');
        }
        
        setIsHeaderStuck(shouldStick);
      }
    };
  
    const scrollListener = () => {
      handleScroll();
    };
  
    const handleResize = () => {
      if (isHeaderStuck && capturedStyles) {
        // Recapture styles on resize
        capturedStyles = null;
        capturedStyles = captureTableStyles();
        
        const stickyWrapper = document.querySelector('.sticky-header-wrapper');
        if (stickyWrapper && capturedStyles) {
          stickyWrapper.style.width = `${capturedStyles.width}px`;
          stickyWrapper.style.left = '50%';
          
          // NEW: Update scale on resize
          const viewportWidth = window.innerWidth;
          let scale = 1;
          
          if (viewportWidth >= 1920) {
            scale = 1.2;
          } else if (viewportWidth >= 1600) {
            scale = 1.1;
          }
          
          stickyWrapper.style.transform = `translateX(calc(-50% + ${capturedStyles.centerOffset}px)) scale(${scale})`;
          
          const stickyTable = stickyWrapper.querySelector('.sticky-table');
          if (stickyTable) {
            stickyTable.style.width = `${capturedStyles.tableWidth}px`;
            stickyTable.style.maxWidth = `${capturedStyles.tableWidth}px`;
          }
        }
      }
    };
  
    window.addEventListener('scroll', scrollListener, { passive: true });
    window.addEventListener('resize', handleResize);
    
    handleScroll();
  
    return () => {
      window.removeEventListener('scroll', scrollListener);
      window.removeEventListener('resize', handleResize);
    };
  }, [isHeaderStuck]);

  // UPDATED useEffect with rate limit handling
  useEffect(() => {
    if (!zip) {
      setLoading(false);
      return;
    }
    
    let isCancelled = false;
    let messageInterval;
    
    (async () => {
      try {
        setLoadingStage('Analyzing your location...');
        
        console.log("ResultsScreen - Sending to API:", { 
          zip, 
          formData, 
          searchType,
          protections,
          communityTypes,
          populationCategories
        });
        
        const messages = [
          'Analyzing your location...',
          'Searching nationwide database...',
          'Evaluating legal protections...',
          'Finding best matches...',
          'Calculating scores...',
          'Almost done...'
        ];
        
        let messageIndex = 0;
        messageInterval = setInterval(() => {
          if (!isCancelled) {
            messageIndex = (messageIndex + 1) % messages.length;
            setLoadingStage(messages[messageIndex]);
          }
        }, 2000);
        
        const res = await api.searchResults({ 
          zip, 
          formData, 
          searchType,
          protections,
          populationCategories,
          tieBreaker, 
          sessionId: state?.sessionId
        });
        
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'Server error');
        
        if (!isCancelled) {
          setData(json);
          setLoading(false);
        }
      } catch (e) {
        if (!isCancelled) {
          // Check if it's a rate limit error
          if (e.isRateLimit) {
            setErr(`Too many searches. Please wait ${e.retryAfter || 60} seconds before trying again.`);
          } else if (e.message) {
            setErr(e.message);
          } else {
            setErr('An error occurred while searching. Please try again.');
          }
          setLoading(false);
        }
      }
    })();
    
    return () => {
      isCancelled = true;
      if (messageInterval) clearInterval(messageInterval);
    };
  }, [zip, formData, searchType, protections, communityTypes, populationCategories, state?.sessionId]);

  // Replace the existing useEffect that handles square heights with this:
  useEffect(() => {
    // Define heights for all 6 combinations at different screen sizes
    // Base heights for 1600px (HP 50cm baseline)
    const SQUARE_HEIGHTS_1600 = {
      basic: {
        both: 1200,
        sexual: 1020,
        gender: 1200
      },
      advanced: {
        both: 2560,
        sexual: 2050,
        gender: 2450
      }
    };
    
    // Heights for 1920px (AOC 60cm)
    const SQUARE_HEIGHTS_1920 = {
      basic: {
        both: 1100,
        sexual: 980,
        gender: 1120
      },
      advanced: {
        both: 2520,
        sexual: 2020,
        gender: 2400
      }
    };
    
    // Heights for 2560px (Extra large)
    const SQUARE_HEIGHTS_2560 = {
      basic: {
        both: 1700,
        sexual: 1450,
        gender: 1700
      },
      advanced: {
        both: 3500,
        sexual: 2800,
        gender: 3300
      }
    };
    
    // Heights for 1280px (Medium desktop)
    const SQUARE_HEIGHTS_1280 = {
      basic: {
        both: 1220,
        sexual: 1030,
        gender: 1230
      },
      advanced: {
        both: 2640,
        sexual: 2100,
        gender: 2530
      }
    };
    
    // Heights for 1024px (Small desktop)
    const SQUARE_HEIGHTS_1024 = {
      basic: {
        both: 850,
        sexual: 730,
        gender: 850
      },
      advanced: {
        both: 1900,
        sexual: 1500,
        gender: 1800
      }
    };

    let resizeTimeout;
    
    const adjustSquareHeights = () => {
      // Clear any pending timeout
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      
      // Debounce the adjustment
      resizeTimeout = setTimeout(() => {
        const square1 = document.querySelector('.results-purple-square');
        const square2 = document.querySelector('.results-purple-square-2');
        
        if (square1 && square2) {
          // Get the appropriate height table based on screen width
          const screenWidth = window.innerWidth;
          let SQUARE_HEIGHTS;
          
          if (screenWidth >= 2560) {
            SQUARE_HEIGHTS = SQUARE_HEIGHTS_2560;
          } else if (screenWidth >= 1920) {
            SQUARE_HEIGHTS = SQUARE_HEIGHTS_1920;
          } else if (screenWidth >= 1600) {
            SQUARE_HEIGHTS = SQUARE_HEIGHTS_1600;
          } else if (screenWidth >= 1280) {
            SQUARE_HEIGHTS = SQUARE_HEIGHTS_1280;
          } else if (screenWidth >= 1024) {
            SQUARE_HEIGHTS = SQUARE_HEIGHTS_1024;
          } else {
            SQUARE_HEIGHTS = SQUARE_HEIGHTS_1600; // Fallback (hidden on mobile anyway)
          }
          
          // Determine protection type
          let protectionType = 'both'; // default
          if (protections.sexualOrientation && protections.genderIdentity) {
            protectionType = 'both';
          } else if (protections.sexualOrientation && !protections.genderIdentity) {
            protectionType = 'sexual';
          } else if (!protections.sexualOrientation && protections.genderIdentity) {
            protectionType = 'gender';
          }
          
          // Get the appropriate height based on search type and protection type
          const finalHeight = SQUARE_HEIGHTS[searchType][protectionType];
          
          // Log for debugging
          console.log('Setting square height:', {
            screenWidth,
            searchType,
            protectionType,
            height: finalHeight,
            protections
          });
          
          // Use requestAnimationFrame to avoid layout thrashing
          requestAnimationFrame(() => {
            square1.style.setProperty('height', `${finalHeight}px`, 'important');
            square2.style.setProperty('height', `${finalHeight}px`, 'important');
          });
        }
      }, 100); // Debounce delay
    };

    // Initial adjustment
    adjustSquareHeights();
    
    // Window resize handler
    const handleResize = () => adjustSquareHeights();
    window.addEventListener('resize', handleResize);
    
    // Data change handler
    if (data) {
      adjustSquareHeights();
    }
    
    // Search options change handler
    if (Object.keys(openSearchOptions).length > 0) {
      setTimeout(adjustSquareHeights, 350);
    }
    
    // ResizeObserver with error handling
    let resizeObserver;
    const resultsCard = document.querySelector('.results-card');
    
    if (resultsCard && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver((entries) => {
        // Use requestAnimationFrame to avoid the error
        window.requestAnimationFrame(() => {
          if (entries[0]?.contentRect) {
            adjustSquareHeights();
          }
        });
      });
      
      resizeObserver.observe(resultsCard);
    }
    
    // Cleanup
    return () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [data, openSearchOptions, searchType, protections]); // Added protections to dependency array

  if (!zip && !data && !loading) {
    return (
      <div className="results-screen-container">
        <div className="results-card">
          <div className="error-state">
            <h1>No ZIP Code Found</h1>
            <p>Please start a new search.</p>
            <button onClick={() => navigate('/')} className="action-button">
              Start New Search
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="results-screen-container">
        <div className="results-card">
          <div className="loading-state enhanced">
            <div className="loading-animation">
              <div className="loading-spinner large"></div>
              <div className="loading-progress">
                <div className="progress-fill"></div>
              </div>
            </div>
            <h1>Finding LGBTQ+ Protections</h1>
            <p className="loading-stage">{loadingStage}</p>
            
            {/* ADD THIS SECTION */}
            {preloadStatus && preloadStatus.status === 'ready' && (
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                background: '#D1FAE5',
                borderRadius: '8px',
                border: '1px solid #10B981',
                fontSize: '0.9rem',
                color: '#065F46'
              }}>
                ✓ Background optimization complete! {preloadStatus.cityCount} cities pre-loaded for faster search.
              </div>
            )}
            
            <div className="loading-tips">
              <p className="tip">
                💡 We're searching through thousands of cities nationwide to find the best matches for your criteria
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="results-screen-container">
        <div className="results-card">
          <div className="error-state">
            <h1>Something Went Wrong</h1>
            <p className="error-message">{err}</p>
            {err && err.includes('Too many searches') && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                background: '#FEF3C7',
                borderRadius: '8px',
                border: '1px solid #F59E0B'
              }}>
                <p style={{ margin: 0, color: '#92400E', fontSize: '0.9rem' }}>
                  <strong>Why am I seeing this?</strong><br />
                  To ensure our service remains fast for everyone, we limit the number of searches 
                  that can be performed in a short time period. This limit will reset automatically.
                </p>
              </div>
            )}
            {data?.message && (
              <p style={{ color: '#6B7280', marginTop: '0.5rem' }}>{data.message}</p>
            )}
            <button onClick={() => navigate('/')} className="action-button">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // NEW: Handle protection mismatches from backend
  if (data?.error === 'protection_mismatch' && data.protectionMismatches) {
    return (
      <div className="results-screen-container">
        <div className="results-card">
          <div className="results-header" style={{ background: '#FEF3C7', borderBottom: '3px solid #F59E0B' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>⚠️</div>
            <h1 style={{ color: '#92400E' }}>Protection Type Mismatch</h1>
            <p style={{ color: '#92400E', fontWeight: 'bold' }}>
              Your search criteria includes protections that don't match your selected protection type
            </p>
          </div>
          
          <div className="no-matches-content" style={{ padding: '2rem' }}>
            <div style={{ 
              background: '#FFF', 
              border: '2px solid #E5E7EB', 
              borderRadius: '12px', 
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ marginBottom: '1rem', color: '#1F2937' }}>Here's what happened:</h3>
              
              <p style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>
                You're searching for <strong style={{ 
                  background: '#DBEAFE', 
                  padding: '2px 8px', 
                  borderRadius: '4px',
                  color: '#1E40AF'
                }}>
                  {protections.sexualOrientation && !protections.genderIdentity ? 'Sexual Orientation' : 
                   !protections.sexualOrientation && protections.genderIdentity ? 'Gender Identity' : 'Both'}
                </strong> protections.
              </p>
              
              <p style={{ marginBottom: '1.5rem' }}>
                But you marked these as <strong>"Must Have"</strong> requirements:
              </p>
              
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {data.protectionMismatches.map((mismatch, idx) => (
                  <li key={idx} style={{
                    background: '#FEE2E2',
                    border: '1px solid #FCA5A5',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'start',
                    gap: '0.75rem'
                  }}>
                    <span style={{ fontSize: '20px', flexShrink: 0 }}>❌</span>
                    <div>
                      <div style={{ fontWeight: 'bold', color: '#991B1B', marginBottom: '0.25rem' }}>
                        {mismatch.label}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#7F1D1D' }}>
                        This protection only applies to <strong>{mismatch.requiredType}</strong>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="action-buttons" style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              <button 
                onClick={() => navigate(-1)} 
                className="action-button secondary"
                style={{ flex: 1 }}
              >
                ← Adjust Search Criteria
              </button>
              <button 
                onClick={() => navigate('/')} 
                className="action-button"
                style={{ flex: 1 }}
              >
                🔍 Start New Search
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ENHANCED: No matches found screen
  if (data && data.matches && data.matches.length === 0) {
    // Check if it's due to population filter
    if (data.noMatchesDueToPopulationFilter) {
      return (
        <div className="results-screen-container">
          <div className="results-card">
            <div className="results-header" style={{ 
              background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)',
              borderBottom: '3px solid #F59E0B'
            }}>
              <div style={{ fontSize: '36px', marginBottom: '8px' }}>🏙️</div>
              <h1 style={{ color: '#92400E', marginBottom: '0.5rem' }}>
                No Matches in Selected City Sizes
              </h1>
              <p style={{ color: '#92400E', fontSize: '1.1rem' }}>
                We couldn't find any cities that meet your criteria in the population ranges you selected
              </p>
            </div>
            
            <div className="no-matches-content" style={{ padding: '2rem' }}>
              {/* Show what they searched for */}
              <div style={{
                background: '#FFF',
                border: '2px solid #E5E7EB',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                <h3 style={{ marginBottom: '1rem' }}>Your Search Criteria:</h3>
                
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Selected city sizes:</strong>
                  <ul style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                    {populationCategories.under10k && <li>Under 10,000</li>}
                    {populationCategories.from10kTo50k && <li>10,000 - 50,000</li>}
                    {populationCategories.from50kTo200k && <li>50,000 - 200,000</li>}
                    {populationCategories.from200kTo1m && <li>200,000 - 1,000,000</li>}
                    {populationCategories.over1m && <li>Over 1,000,000</li>}
                  </ul>
                </div>
                
                {data.potentialMatchesWithoutFilter && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    background: '#F0F9FF',
                    borderRadius: '8px',
                    border: '1px solid #BAE6FD'
                  }}>
                    <p style={{ margin: 0 }}>
                      <strong>Potential matches without population filter:</strong> {data.potentialMatchesWithoutFilter}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Action buttons */}
              <div className="action-buttons" style={{ display: 'flex', gap: '1rem' }}>
                <button 
                  onClick={() => navigate('/')} 
                  className="action-button secondary"
                  style={{ flex: 1 }}
                >
                  ← Start New Search
                </button>
                <button 
                  onClick={() => {
                    // Navigate back with all population categories selected
                    navigate('/', {
                      state: {
                        zipCode: zip,
                        expandPopulationFilter: true
                      }
                    });
                  }} 
                  className="action-button"
                  style={{ flex: 1 }}
                >
                  🔍 Search All City Sizes
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Enhanced "no matches" screen with best available results
    const dealBreakerLabels = mustHaveFields.map(field => getSearchOptionLabel(field)).filter(label => label);
    const bestAvailable = data.bestAvailable || [];

    return (
      <div className="results-screen-container">
        <div className="results-card">
          <div className="results-header" style={{ 
            background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)',
            borderBottom: '3px solid #F59E0B'
          }}>
            <div style={{ fontSize: '36px', marginBottom: '8px' }}>⚠️</div>
            <h1 style={{ color: '#92400E', marginBottom: '0.5rem' }}>
              No Perfect Matches Found
            </h1>
            <p style={{ color: '#92400E', fontSize: '1.1rem' }}>
              No locations meet all your "Must Have" requirements
            </p>
          </div>
          
          <div className="no-matches-content" style={{ padding: '2rem' }}>
            {/* Current location info */}
            <div className="current-location-info" style={{
              background: '#F9FAFB',
              padding: '1.5rem',
              borderRadius: '12px',
              marginBottom: '2rem',
              border: '1px solid #E5E7EB'
            }}>
              <h3 style={{ marginBottom: '0.5rem' }}>Your Current Location:</h3>
              <h4 style={{ color: '#374151', marginBottom: '0.5rem' }}>
                {data.current.city}, {data.current.state_id} ({data.current.zip})
              </h4>
              {data.current.community_type && (
                <p style={{ color: '#6B7280', fontSize: '0.9rem', margin: 0 }}>
                  Community Type: {data.current.community_type}
                </p>
              )}
            </div>
            
            {/* Unmet requirements */}
            <div style={{
              background: '#FEE2E2',
              border: '1px solid #FCA5A5',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem'
            }}>
              <h3 style={{ color: '#991B1B', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '24px' }}>❌</span>
                Your Required Protections (Deal Breakers):
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {dealBreakerLabels.map((label, idx) => (
                  <li key={idx} style={{
                    background: '#FFFFFF',
                    border: '1px solid #F87171',
                    borderLeft: '4px solid #DC2626',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '0.75rem',
                    fontWeight: '600',
                    color: '#7F1D1D'
                  }}>
                    {label}
                  </li>
                ))}
              </ul>
              <p style={{ 
                marginTop: '1rem', 
                marginBottom: 0, 
                color: '#7F1D1D', 
                fontSize: '0.9rem',
                fontStyle: 'italic'
              }}>
                No locations in our database currently meet all these requirements together.
              </p>
            </div>

            {/* Action buttons - UPDATED: Forces page reload */}
            <div className="action-buttons" style={{ 
              display: 'flex', 
              gap: '1rem', 
              marginBottom: '2rem',
              justifyContent: 'center'
            }}>
              <button 
                onClick={() => {
                  // Store the data in sessionStorage before navigating
                  const returnData = {
                    zipCode: zip || data?.current?.zip || state?.zipCode,
                    protections: protections || state?.protections,
                    populationCategories: populationCategories || state?.populationCategories,
                    returningFromNoMatches: true
                  };
                  
                  // Save to sessionStorage (survives page reload)
                  sessionStorage.setItem('pridepath_return_data', JSON.stringify(returnData));
                  
                  // Navigate to home page
                  window.location.href = '/';
                  // This forces a full page reload instead of React Router navigation
                }} 
                className="action-button"
                style={{
                  padding: '0.75rem 2rem',
                  background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                ← Start New Search
              </button>
            </div>

            {/* Suggestions */}
            <div className="suggestions" style={{
              background: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem'
            }}>
              <h4 style={{ color: '#1F2937', marginBottom: '1rem' }}>
                💭 Suggestions to Find More Matches:
              </h4>
              <ul style={{ marginLeft: '1.5rem', color: '#4B5563' }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  Try reducing the number of "Must Have" requirements - change some to "Good to Have" instead
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Consider expanding your city size preferences to include more population ranges
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Check if local city ordinances provide additional protections not reflected in state law
                </li>
                <li>
                  Use our City Comparison tool to evaluate specific cities you're considering
                </li>
              </ul>
            </div>
            
            {/* Resources section */}
            <div className="resources-section" style={{
              background: '#F3F4F6',
              padding: '1.5rem',
              borderRadius: '12px'
            }}>
              <h4 style={{ color: '#1F2937', marginBottom: '1rem' }}>
                📚 Additional Resources:
              </h4>
              <p style={{ color: '#4B5563', marginBottom: '1rem' }}>
                Find LGBTQ+ advocacy organizations and support in your area:
              </p>
              <a
                href={nearMeUrl(zip)}
                target="_blank"
                rel="noopener noreferrer"
                className="resource-link"
                style={{
                  color: '#2563EB',
                  fontWeight: '600',
                  textDecoration: 'underline'
                }}
              >
                🔍 Find Local LGBTQ+ Resources Near {zip}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const columns = [data.current, ...(data.matches || [])];
  const prioritizedSearchOptions = getPrioritizedSearchOptionRows();

  return (
    <div className={`results-screen-container ${printMode ? 'print-mode' : ''}`}>
      <Helmet>
        <title>Search Results | LGBT PridePath</title>
        <meta name="description" content="View LGBTQ+ protection results for communities matching your criteria. Compare legal protections, local resources, and community information." />
        <link rel="canonical" href="https://www.lgbtpridepath.org/results" />
      </Helmet>

      <div className="wave-wrapper"></div>
      <div className="extra-waves"></div>
      
      {/* Additional static wave layers */}
      <div className="wave-layer-static wave-1"></div>
      <div className="wave-layer-static wave-2"></div>
      <div className="wave-layer-static wave-3"></div>
      <div className="wave-layer-static wave-4"></div>

      {/* ADD THESE TWO LINES - Purple decorative squares */}
      <div className="results-purple-square"></div>
      <div className="results-purple-square-2"></div>

      <style>
        {`
          .status-cell.partial::before {
            background: var(--partial-bg, #FEF3C7) !important;
            border-color: var(--partial-border, #F59E0B) !important;
            color: var(--partial-text, #92400E) !important;
          }
          
          .loading-state.enhanced {
            text-align: center;
            padding: 3rem;
          }
          
          .loading-animation {
            margin-bottom: 2rem;
          }
          
          .loading-spinner.large {
            width: 60px;
            height: 60px;
            border-width: 4px;
            margin: 0 auto 1.5rem;
          }
          
          .loading-progress {
            width: 200px;
            height: 6px;
            background: #e9ecef;
            border-radius: 3px;
            margin: 0 auto;
            overflow: hidden;
          }
          
          .progress-fill {
            height: 100%;
            background: linear-gradient(90deg, #007bff, #0056b3);
            border-radius: 3px;
            animation: progress 2s ease-in-out infinite;
          }
          
          @keyframes progress {
            0% { width: 0%; }
            50% { width: 70%; }
            100% { width: 100%; }
          }
          
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          
          .loading-stage {
            font-size: 1.1rem;
            color: #666;
            margin: 1rem 0;
          }
          
          .loading-tips {
            margin-top: 2rem;
            padding: 1rem;
            background: #f8f9fa;
            border-radius: 8px;
          }
          
          .loading-tips .tip {
            margin: 0;
            color: #666;
            font-size: 0.9rem;
          }
          
          .section-header-row td {
            background: linear-gradient(135deg, #E0E7FF, #C7D2FE);
            padding: 0.75rem;
            font-weight: 700;
            color: #4338CA;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-size: 0.85rem;
            border-top: 2px solid #C7D2FE;
            border-bottom: 1px solid #C7D2FE;
          }
          
          .classification-icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 14px;
            height: 14px;
            margin-left: 4px;
            background: #6B7280;
            color: white;
            border-radius: 50%;
            font-size: 10px;
            font-weight: bold;
            cursor: help;
            vertical-align: middle;
            transition: all 0.2s ease;
            flex-shrink: 0;
          }
          
          .classification-icon:hover {
            background: #4B5563;
            transform: scale(1.1);
          }
          
          /* Loading spinner for resources */
          .resource-loading-spinner {
            display: inline-block;
            width: 12px;
            height: 12px;
            border: 2px solid #E5E7EB;
            border-left-color: #6366F1;
            border-radius: 50%;
            animation: resource-spin 0.8s linear infinite;
          }
          
          @keyframes resource-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          /* Smooth fade-in animation when resources load */
          .resource-loaded {
            animation: fadeIn 0.3s ease-in;
          }
          
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-2px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <div className="results-card">
        <div className="results-header">
          <h1>LGBTQ+ Protection Results</h1>
          <p>Legal protections and resources for ZIP {zip}</p>
          <p style={{ fontSize: '0.9rem', color: '#FFF', marginTop: '0.3rem' }}>
            City sizes: {getSelectedPopulationCategories()}
          </p>
          <p style={{ fontSize: '0.9rem', color: '#FFF', marginTop: '0.5rem' }}>
            Protection Types: {getSelectedProtectionTypes()}
          </p>
          
          {tieBreaker && tieBreaker !== '' && (
            <p style={{ fontSize: '0.9rem', color: '#FFF', marginTop: '0.3rem' }}>
              Tie-Breaking Preference: {getTieBreakerDisplay()}
            </p>
          )}
          
          <div className="header-actions">
            <button className="action-button secondary" onClick={handlePrint}>
              🖨️ Print Results
            </button>
            <button className="action-button" onClick={() => navigate('/')}>
              🔍 New Search
            </button>
          </div>
        </div>
        
        {/* <MappingDebugger 
          formData={formData} 
          selectedSearchOptions={selectedSearchOptions}
          onToggleExpansion={setDisableExpansion}
          onToggleInvertedTags={setHideInvertedTags}
          onPartialColorChange={setPartialColorScheme}
          searchType={searchType}
          locations={columns}
          protections={protections}
          communityTypes={communityTypes}
          populationCategories={populationCategories}
          searchMetadata={data.searchMetadata}
          tieBreaker={tieBreaker}
        /> */}
        <div className="results-view">
          {isHeaderStuck && (
            <div className="sticky-header-wrapper">
              <table className="results-table sticky-table">
                <thead>
                  <tr>
                    <th className="protection-header">Protection Category</th>
                    {columns.map((col, i) => (
                      <th key={col.id} className="location-col">
                        <div className="location-header">
                        <div className={`location-tag ${i === 0 ? 'current' : 'match'}`}>
                          {i === 0 ? 'Current Location' : `Match ${i}`}
                        </div>
                        <div className="location-name">
                          {col.city}, {col.state_id}
                        </div>
                        {/* <div className="location-zip">
                          {col.zip}
                        </div> */}
                      </div>
                    </th>
                    ))}
                  </tr>
                </thead>
              </table>
            </div>
          )}
          <div className={`results-table-container ${isHeaderStuck ? 'header-stuck' : ''}`}>
            <table className="results-table">
              <thead className={isHeaderStuck ? 'is-stuck' : ''}>
                <tr>
                  <th className="protection-header">Protection Category</th>
                  {columns.map((col, i) => (
                    <th key={col.id} className="location-col">
                      <div className="location-header">
                        <div className={`location-tag ${i === 0 ? 'current' : 'match'}`}>
                          {i === 0 ? 'Current Location' : `Match ${i}`}
                        </div>
                        <div className="location-name">
                          {col.label.split('\n')[1] || col.label}
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { key: 'distance', label: 'DISTANCE FROM CURRENT' },
                  { key: 'incomeComparison', label: 'INCOME COMPARISON' },
                  { key: 'rentComparison', label: 'RENT COMPARISON' },
                  { key: 'population', label: 'CITY POPULATION' },
                  { key: 'communityCenters', label: 'COMMUNITY CENTERS' },
                  { key: 'lgbtnearMe', label: 'Local LGBT Resources' },
                ].map(({ key, label }) => (
                  <tr key={key} className="info-row">
                    <td className="category-label">{label}</td>
                    {columns.map((col, idx) => {
                      const val = col[key];
                      let cell;

                      if (key === 'distance') {
                        cell = idx === 0 ? 'Current' : val ? `${Math.round(val)} miles` : 'N/A';
                      } else if (key === 'incomeComparison') {
                        if (idx === 0) {
                          const currentIncome = col.income_household_median || col.median_household_income;
                          const cityWideTooltip = getCityWideDataTooltip(col);
                          
                          if (currentIncome) {
                            cell = (
                              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                ${currentIncome.toLocaleString()}
                                <Tooltip 
                                  content={
                                    cityWideTooltip ? (
                                      <div>
                                        <div>Median Household Income: ${currentIncome.toLocaleString()}</div>
                                        <div style={{ marginTop: '4px', fontSize: '0.9em', fontStyle: 'italic' }}>
                                          {cityWideTooltip}
                                        </div>
                                      </div>
                                    ) : (
                                      `Median Household Income: ${currentIncome.toLocaleString()}`
                                    )
                                  } 
                                  preferredSide="right"
                                >
                                  <span className="results-info-icon">ⓘ</span>
                                </Tooltip>
                              </span>
                            );
                          } else {
                            cell = 'No Data';
                          }
                        } else {
                          const currentIncome = data.current.income_household_median || data.current.median_household_income;
                          const compareIncome = col.income_household_median || col.median_household_income;
                          const cityWideTooltip = getCityWideDataTooltip(col);
                          
                          if (!currentIncome || !compareIncome) {
                            cell = 'No Data';
                          } else {
                            const diff = ((compareIncome - currentIncome) / currentIncome) * 100;
                            const absDiff = Math.abs(diff);
                            
                            if (absDiff < 0.5) {
                              cell = (
                                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                  0% Difference
                                  <Tooltip 
                                    content={
                                      cityWideTooltip ? (
                                        <div>
                                          <div>Median Household Income: ${compareIncome.toLocaleString()}</div>
                                          <div style={{ marginTop: '4px', fontSize: '0.9em', fontStyle: 'italic' }}>
                                            {cityWideTooltip}
                                          </div>
                                        </div>
                                      ) : (
                                        `Median Household Income: ${compareIncome.toLocaleString()}`
                                      )
                                    } 
                                    preferredSide="right"
                                  >
                                    <span className="results-info-icon">ⓘ</span>
                                  </Tooltip>
                                </span>
                              );
                            } else if (diff > 0) {
                              cell = (
                                <span className="income-higher">
                                  {absDiff.toFixed(0)}% Higher
                                  <Tooltip 
                                    content={
                                      cityWideTooltip ? ( 
                                        <div>
                                          <div>Median Household Income: ${compareIncome.toLocaleString()}</div>
                                          <div style={{ marginTop: '4px', fontSize: '0.9em', fontStyle: 'italic' }}>
                                            {cityWideTooltip}
                                          </div>
                                        </div>
                                      ) : (
                                        `Median Household Income: ${compareIncome.toLocaleString()}`
                                      )
                                    } 
                                    preferredSide="right"
                                  >
                                    <span className="results-info-icon">ⓘ</span>
                                  </Tooltip>
                                </span>
                              );
                            } else {
                              cell = (
                                <span className="income-lower">
                                  {absDiff.toFixed(0)}% Lower
                                  <Tooltip 
                                    content={
                                      cityWideTooltip ? (
                                        <div>
                                          <div>Median Household Income: ${compareIncome.toLocaleString()}</div>
                                          <div style={{ marginTop: '4px', fontSize: '0.9em', fontStyle: 'italic' }}>
                                            {cityWideTooltip}
                                          </div>
                                        </div>
                                      ) : (
                                        `Median Household Income: ${compareIncome.toLocaleString()}`
                                      )
                                    } 
                                    preferredSide="right"
                                  >
                                    <span className="results-info-icon">ⓘ</span>
                                  </Tooltip>
                                </span>
                              );
                            }
                          }
                        }
                      } else if (key === 'rentComparison') {
                        if (idx === 0) {
                          const currentRent = col.rent_median;
                          const cityWideTooltip = getCityWideDataTooltip(col);
                          
                          if (currentRent) {
                            cell = (
                              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                ${currentRent.toLocaleString()}/mo
                                <Tooltip 
                                  content={
                                    cityWideTooltip ? (
                                      <div>
                                        <div>Median Monthly Rent: ${currentRent.toLocaleString()}</div>
                                        <div style={{ marginTop: '4px', fontSize: '0.9em', fontStyle: 'italic' }}>
                                          {cityWideTooltip}
                                        </div>
                                      </div>
                                    ) : (
                                      `Median Monthly Rent: ${currentRent.toLocaleString()}`
                                    )
                                  } 
                                  preferredSide="right"
                                >
                                  <span className="results-info-icon">ℹ</span>
                                </Tooltip>
                              </span>
                            );
                          } else {
                            cell = 'No Data';
                          }
                        } else {
                          const currentRent = data.current.rent_median;
                          const compareRent = col.rent_median;
                          const cityWideTooltip = getCityWideDataTooltip(col);
                          
                          if (!currentRent || !compareRent) {
                            cell = 'No Data';
                          } else {
                            const diff = ((compareRent - currentRent) / currentRent) * 100;
                            const absDiff = Math.abs(diff);
                            
                            if (absDiff < 0.5) {
                              cell = (
                                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                  0% Difference
                                  <Tooltip 
                                    content={
                                      cityWideTooltip ? (
                                        <div>
                                          <div>Median Monthly Rent: ${compareRent.toLocaleString()}</div>
                                          <div style={{ marginTop: '4px', fontSize: '0.9em', fontStyle: 'italic' }}>
                                            {cityWideTooltip}
                                          </div>
                                        </div>
                                      ) : (
                                        `Median Monthly Rent: ${compareRent.toLocaleString()}`
                                      )
                                    } 
                                    preferredSide="right"
                                  >
                                    <span className="results-info-icon">ℹ</span>
                                  </Tooltip>
                                </span>
                              );
                            } else if (diff < 0) {
                              // If diff < 0, compareRent is LOWER than currentRent, so it's CHEAPER
                              cell = (
                                <span className="rent-cheaper">
                                  {absDiff.toFixed(0)}% Cheaper
                                  <Tooltip 
                                    content={
                                      cityWideTooltip ? (
                                        <div>
                                          <div>Median Monthly Rent: ${compareRent.toLocaleString()}</div>
                                          <div style={{ marginTop: '4px', fontSize: '0.9em', fontStyle: 'italic' }}>
                                            {cityWideTooltip}
                                          </div>
                                        </div>
                                      ) : (
                                        `Median Monthly Rent: ${compareRent.toLocaleString()}`
                                      )
                                    } 
                                    preferredSide="right"
                                  >
                                    <span className="results-info-icon">ℹ</span>
                                  </Tooltip>
                                </span>
                              );
                            } else {
                              // If diff > 0, compareRent is HIGHER than currentRent, so it's MORE EXPENSIVE
                              cell = (
                                <span className="rent-higher">
                                  {absDiff.toFixed(0)}% Higher
                                  <Tooltip 
                                    content={
                                      cityWideTooltip ? (
                                        <div>
                                          <div>Median Monthly Rent: ${compareRent.toLocaleString()}</div>
                                          <div style={{ marginTop: '4px', fontSize: '0.9em', fontStyle: 'italic' }}>
                                            {cityWideTooltip}
                                          </div>
                                        </div>
                                      ) : (
                                        `Median Monthly Rent: ${compareRent.toLocaleString()}`
                                      )
                                    } 
                                    preferredSide="right"
                                  >
                                    <span className="results-info-icon">ℹ</span>
                                  </Tooltip>
                                </span>
                              );
                            }
                          }
                        }
                      } else if (key === 'population') {
                        // Check for different possible field names your backend might use
                        const population = col.city_population || col.population || col.totalPopulation;
                        
                        if (population) {
                          // Make sure it's a number and format with commas
                          const popNumber = typeof population === 'string' ? parseInt(population, 10) : population;
                          cell = popNumber.toLocaleString('en-US');
                        } else {
                          // Fallback to community type if no population data
                          cell = col.community_type || 'N/A';
                        }
                      } else if (key === 'communityCenters') {
                        cell = getCommunityCentersWithLoading(col);
                      } else if (key === 'lgbtnearMe') {
                        cell = (
                          <a
                            href={nearMeUrl(col.zip)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="resource-link"
                          >
                            {getResourceDisplayWithLoading(col)}
                          </a>
                        );
                      } else {
                        cell = val || 'N/A';
                      }

                      return <td key={col.id} className="info-cell">{cell}</td>;
                    })}
                  </tr>
                ))}
                <tr className="divider-row">
                  <td colSpan={columns.length + 1}>
                    <div className="divider-content">Legal Protections</div>
                  </td>
                </tr>
                
                {searchType === 'advanced' ? (
                  prioritizedSearchOptions.map((section, sectionIdx) => (
                    <React.Fragment key={sectionIdx}>
                      <tr className="section-header-row">
                        <td colSpan={columns.length + 1}>{section.header}</td>
                      </tr>
                      {section.options.map((row) => {
                        const classification = getSearchOptionClassificationSummary(row.searchOption);
                        return (
                          <tr 
                            key={row.searchOption}
                            className={`protection-row ${row.isDealBreaker ? 'must-have' : ''} ${formData[row.searchOption] === 'doesnt-matter' ? 'doesnt-matter' : ''}`}
                          >
                            <td className="category-label">
                              <div className="protection-label-container">
                                <span className="protection-label">
                                  {row.label}
                                  {row.isDealBreaker && <span className="priority-indicator" style={{ color: '#FF0000' }}> *Deal Breaker* </span>}
                                  {/* Classification icon - COMMENTED OUT
                                  <Tooltip 
                                    content={classification.text}
                                    preferredSide="right"
                                  >
                                    <span className="classification-icon">ⓘ</span>
                                  </Tooltip>
                                  */}
                                </span>
                              </div>
                            </td>
                            {columns.map((col) => {
                              const status = getSearchOptionStatus(col, row.searchOption);
                              const partialStyles = getPartialColorStyles();
                              return (
                                <td 
                                  key={col.id} 
                                  className={`status-cell ${status}`}
                                  style={{
                                    '--partial-bg': partialStyles.background,
                                    '--partial-border': partialStyles.border,
                                    '--partial-text': partialStyles.text
                                  }}
                                >
                                  <span className="status-indicator"></span>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))
                ) : (
                  prioritizedSearchOptions.map((row) => {
                    const expanded = openSearchOptions[row.searchOption];
                    const advancedOptions = basicToAdvancedMapping[row.searchOption] || [];
                    const classification = getSearchOptionClassificationSummary(row.searchOption);
                    
                    return (
                      <React.Fragment key={row.searchOption}>
                        <tr 
                          className={`protection-row ${row.isDealBreaker ? 'must-have' : ''} ${
                            openSearchOptions[row.searchOption] ? 'expanded' : ''
                          } ${formData[row.searchOption] === 'doesnt-matter' ? 'doesnt-matter' : ''}`}
                          onClick={() => toggleSearchOption(row.searchOption)}
                        >
                          <td className="category-label">
                            <div className="protection-label-container">
                              <span className="protection-label">
                                {row.label}
                                {row.isDealBreaker && <span className="priority-indicator" style={{ color: '#FF0000' }}> *Deal Breaker* </span>}
                                {/* Classification icon - COMMENTED OUT
                                <Tooltip 
                                  content={classification.text}
                                  preferredSide="right"
                                >
                                  <span className="classification-icon">ⓘ</span>
                                </Tooltip>
                                */}
                              </span>
                              {!disableExpansion && advancedOptions.length > 0 && (
                                <button 
                                  className="view-details-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleSearchOption(row.searchOption);
                                  }}
                                >
                                  {openSearchOptions[row.searchOption] ? 'HIDE\nDETAILS' : 'VIEW\nDETAILS'}
                                </button>
                              )}
                            </div>
                          </td>
                          {columns.map((col) => {
                            const status = getSearchOptionStatus(col, row.searchOption);
                            const partialStyles = getPartialColorStyles();
                            return (
                              <td 
                                key={col.id} 
                                className={`status-cell ${status}`}
                                style={{
                                  '--partial-bg': partialStyles.background,
                                  '--partial-border': partialStyles.border,
                                  '--partial-text': partialStyles.text
                                }}
                              >
                                <span className="status-indicator"></span>
                              </td>
                            );
                          })}
                        </tr>
                        {!disableExpansion && openSearchOptions[row.searchOption] && advancedOptions.length > 0 && (
                          <tr className="law-details-row">
                            <td colSpan={columns.length + 1}>
                              <div className="law-details-container">
                                <div className="law-details-header">
                                  <h4>Legal Protections in Detail:</h4>
                                </div>
                                <div className="law-details-grid">
                                  {advancedOptions
                                    .filter(advancedOption => shouldShowSearchOption(advancedOption, protections))
                                    .map((advancedOption) => {
                                      const advancedLabel = getSearchOptionLabel(advancedOption);
                                      const advancedClassification = getSearchOptionClassificationSummary(advancedOption);
                                      return (
                                        <div key={advancedOption} className="law-detail-item">
                                          <div className="law-detail-label">
                                            {advancedLabel}
                                            <Tooltip 
                                              content={advancedClassification.text}
                                              preferredSide="right"
                                            >
                                              <span className="classification-icon">ⓘ</span>
                                            </Tooltip>
                                          </div>

                                          <div className="law-statuses" style={{ '--location-count': columns.length }}>
                                            {columns.map((col, idx) => {
                                              const status = getSearchOptionStatus(col, advancedOption);
                                              const partialStyles = getPartialColorStyles();
                                              return (
                                                <div 
                                                  key={col.id} 
                                                  className={`law-status ${status}`}
                                                  style={{
                                                    '--partial-bg': partialStyles.background,
                                                    '--partial-border': partialStyles.border,
                                                    '--partial-text': partialStyles.text
                                                  }}
                                                >
                                                  <span className="status-badge">{status.toUpperCase()}</span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsScreen;